import { createElement, isNullOrUndefined, getValue, extend, append } from '@syncfusion/ej2-base';
import * as cls from '../base/css-constants';
import { DataUtil } from '@syncfusion/ej2-data';
import { getUniversalTime } from '../base/utils';
/**
 * Configures the `Timeline` of the gantt.
 */
export class Timeline {
    constructor(ganttObj) {
        this.isZoomIn = false;
        this.isZooming = false;
        this.isZoomToFit = false;
        this.topTierCollection = [];
        this.bottomTierCollection = [];
        this.pdfExportTopTierCollection = [];
        this.pdfExportBottomTierCollection = [];
        this.restrictRender = true;
        this.applyDstHour = false;
        this.performedTimeSpanAction = false;
        this.dstIncreaseHour = false;
        this.fromDummyDate = false;
        this.isZoomedToFit = false;
        this.isZoomingAction = false;
        this.increaseIteration = false;
        this.isFirstLoop = false;
        this.inconsistenceDstApplied = false;
        this.parent = ganttObj;
        this.initProperties();
    }
    /**
     * To initialize the public property.
     *
     * @returns {void}
     * @private
     */
    initProperties() {
        this.timelineStartDate = null;
        this.timelineEndDate = null;
        this.totalTimelineWidth = 0;
        this.customTimelineSettings = null;
        this.parent.isTimelineRoundOff = this.isZoomToFit ? false : (this.parent.timelineSettings.viewEndDate !== 'auto'
            || this.parent.timelineSettings.viewStartDate !== 'auto' || !isNullOrUndefined(this.parent.projectStartDate)) ? false : true;
        if (this.parent.enablePersistence && this.parent.isLoad) {
            this.parent.timelineSettings = this.parent.currentZoomingLevel;
        }
    }
    /**
     * To render timeline header series.
     *
     * @returns {void}
     * @private
     */
    validateTimelineProp() {
        this.roundOffDays();
        this.processTimelineProperty();
        this.timelineWidthCalculation();
    }
    /**
     * Function used to refresh Gantt rows.
     *
     * @returns {void}
     * @private
     */
    refreshTimeline() {
        this.initProperties();
        this.processTimelineUnit();
        if (!this.parent.undoRedoModule || (this.parent.undoRedoModule && !this.parent.undoRedoModule['isZoomingUndoRedoProgress'])) {
            this.parent.dataOperation.calculateProjectDates();
        }
        if (!this.parent.isFromOnPropertyChange) {
            const viewStartAuto = this.parent.timelineSettings.viewStartDate === 'auto';
            const viewEndAuto = this.parent.timelineSettings.viewEndDate === 'auto';
            const projectEndUndefined = isNullOrUndefined(this.parent.projectEndDate);
            if (!viewStartAuto && (!viewEndAuto || (viewEndAuto && projectEndUndefined)) && !this.isZoomToFit) {
                this.parent.updateProjectDates(this.parent.cloneTimelineStartDate, this.parent.cloneTimelineEndDate, this.parent.isTimelineRoundOff);
            }
            else if (viewStartAuto && !this.isZoomToFit && projectEndUndefined) {
                this.parent.updateProjectDates(this.parent.cloneProjectStartDate, this.parent.cloneTimelineEndDate, this.parent.isTimelineRoundOff);
            }
            else if (!viewStartAuto && viewEndAuto && !this.isZoomToFit && !projectEndUndefined) {
                this.parent.updateProjectDates(this.parent.cloneTimelineStartDate, this.parent.cloneProjectEndDate, this.parent.isTimelineRoundOff);
            }
            else {
                this.parent.updateProjectDates(this.parent.cloneProjectStartDate, this.parent.cloneProjectEndDate, this.parent.isTimelineRoundOff);
            }
        }
        const timelineContainer = this.parent.element.getElementsByClassName('e-timeline-header-container')[0]['offsetHeight'];
        this.parent.element.getElementsByClassName('e-gridcontent')[0]['style'].height = 'calc(100% - ' + timelineContainer + 'px)';
        this.parent.element.getElementsByClassName('e-chart-scroll-container e-content')[0]['style'].height = 'calc(100% - ' + timelineContainer + 'px)';
    }
    /**
     * Function used to refresh Gantt rows.
     *
     * @returns {void}
     * @private
     */
    refreshTimelineByTimeSpan() {
        this.validateTimelineProp();
        if (!this.parent.pdfExportModule || (this.parent.pdfExportModule && !this.parent.pdfExportModule.isPdfExport) ||
            (this.parent.pdfExportModule && this.parent.pdfExportModule.isPdfExport &&
                this.parent.pdfExportModule.helper.exportProps &&
                !this.parent.pdfExportModule.helper.exportProps.fitToWidthSettings.isFitToWidth)) {
            this.parent.ganttChartModule.chartTimelineContainer.innerHTML = '';
        }
        this.createTimelineSeries();
    }
    /**
     * Function used to refresh Gantt rows.
     *
     * @returns {void}
     * @private
     */
    updateChartByNewTimeline() {
        this.parent.chartRowsModule.refreshChartByTimeline();
        const currentScrollLeft = this.parent.element.getElementsByClassName('e-chart-scroll-container e-content')[0].scrollLeft;
        this.parent.element.getElementsByClassName('e-timeline-header-container')[0].scrollLeft = currentScrollLeft;
        this.parent.notify('refreshDayMarkers', {});
    }
    /**
     * Function used to perform Zoomin and Zoomout actions in Gantt control.
     *
     * @param {boolean} isZoomIn .
     * @private
     * @returns {void}
     */
    processZooming(isZoomIn) {
        if (this.parent.isReact) {
            this.parent['clearTemplate'](['TaskbarTemplate', 'ParentTaskbarTemplate', 'MilestoneTemplate', 'TaskLabelTemplate', 'RightLabelTemplate', 'LeftLabelTemplate']);
        }
        this.isZoomToFit = this.isZoomedToFit = false;
        this.updateUndoRedo(isZoomIn);
        if (!this.parent['isProjectDateUpdated']) {
            this.parent.dateValidationModule.calculateProjectDates();
        }
        if (this.parent.zoomingProjectStartDate) {
            this.parent.cloneProjectStartDate = this.parent.zoomingProjectStartDate;
            this.parent.cloneProjectEndDate = this.parent.zoomingProjectEndDate;
        }
        this.parent.zoomingProjectStartDate = this.parent.zoomingProjectEndDate = null;
        const currentZoomingLevel = this.checkCurrentZoomingLevel();
        this.isZoomIn = isZoomIn;
        this.isZooming = true;
        let currentLevel = this.getZoomLevel(currentZoomingLevel, isZoomIn);
        this.updateToolbar(currentLevel, isZoomIn);
        currentLevel = this.parent.zoomingLevels.findIndex((tempLevel) => {
            return tempLevel.level === currentLevel;
        });
        let newTimeline = this.parent.zoomingLevels[currentLevel];
        const args = {
            requestType: isZoomIn ? 'beforeZoomIn' : 'beforeZoomOut',
            timeline: newTimeline,
            cancel: false
        };
        this.parent.trigger('actionBegin', args);
        if (!args.cancel) {
            this.parent['showLoadingIndicator']();
            newTimeline = args.timeline;
            this.changeTimelineSettings(newTimeline);
        }
        this.isZooming = false;
        this.updateTimelineSettingsAfterZooming();
    }
    updateUndoRedo(isZoomIn) {
        const action = isZoomIn ? 'ZoomIn' : 'ZoomOut';
        if (this.parent.undoRedoModule && this.parent['isUndoRedoItemPresent'](action)) {
            if (this.parent.undoRedoModule['redoEnabled']) {
                this.parent.undoRedoModule['disableRedo']();
            }
            this.parent.undoRedoModule['createUndoCollection']();
            const previousTimeline = {
                action,
                previousZoomingLevel: extend({}, {}, this.parent.currentZoomingLevel, true)
            };
            previousTimeline['previousZoomingLevel'].bottomTier.format = this.customTimelineSettings.bottomTier.format;
            previousTimeline['previousZoomingLevel'].topTier.format = this.customTimelineSettings.topTier.format;
            this.parent.undoRedoModule['getUndoCollection'][this.parent.undoRedoModule['getUndoCollection'].length - 1] = previousTimeline;
        }
    }
    getZoomLevel(currentZoomingLevel, isZoomIn) {
        const levelChange = isZoomIn ? 1 : -1;
        const level = currentZoomingLevel + levelChange;
        const foundLevel = this.parent.zoomingLevels.find((tempLevel) => tempLevel.level === level);
        return foundLevel ? level : currentZoomingLevel;
    }
    updateToolbar(currentLevel, isZoomIn) {
        if (this.parent.toolbarModule) {
            if (isZoomIn) {
                if (currentLevel === this.parent.zoomingLevels[this.parent.zoomingLevels.length - 1].level) {
                    this.parent.toolbarModule.enableItems([this.parent.controlId + '_zoomin'], false); // disable toolbar items.
                    this.parent.toolbarModule.enableItems([this.parent.controlId + '_zoomout'], true);
                }
                else {
                    this.parent.toolbarModule.enableItems([this.parent.controlId + '_zoomout'], true); // disable toolbar items.
                }
            }
            else {
                if (currentLevel === this.parent.zoomingLevels[0].level) {
                    this.parent.toolbarModule.enableItems([this.parent.controlId + '_zoomout'], false); // disable toolbar items.
                    this.parent.toolbarModule.enableItems([this.parent.controlId + '_zoomin'], true);
                }
                else {
                    this.parent.toolbarModule.enableItems([this.parent.controlId + '_zoomin'], true); // enable toolbar items.
                }
            }
        }
    }
    /**
     * To change the timeline settings property values based upon the Zooming levels.
     *
     * @param {ZoomTimelineSettings} newTimeline .
     * @returns {void}
     * @private
     */
    changeTimelineSettings(newTimeline) {
        if (this.isZoomToFit || this.isZooming) {
            this.isSingleTier = this.customTimelineSettings.topTier.unit === 'None' || this.customTimelineSettings.bottomTier.unit === 'None' ? true : false;
        }
        else {
            this.isSingleTier = newTimeline.topTier.unit === 'None' || newTimeline.bottomTier.unit === 'None' ? true : false;
        }
        const skipProperty = this.isSingleTier ?
            this.customTimelineSettings.topTier.unit === 'None' ?
                'topTier' : 'bottomTier' : null;
        Object.keys(this.customTimelineSettings).forEach((property) => {
            if (property !== skipProperty) {
                this.customTimelineSettings[property] = (typeof newTimeline[property] === 'object'
                    && !isNullOrUndefined(newTimeline[property])) ? Object.assign({}, newTimeline[property]) : newTimeline[property];
            }
            else {
                const value = property === 'topTier' ? 'bottomTier' : 'topTier';
                const assignValue = 'bottomTier';
                if (newTimeline[`${assignValue}`].unit !== 'None') {
                    this.customTimelineSettings[value] = Object.assign({}, newTimeline[assignValue]);
                }
            }
        });
        this.parent.isTimelineRoundOff = this.isZoomToFit ? false : (this.parent.timelineSettings.viewEndDate !== 'auto' ||
            this.parent.timelineSettings.viewStartDate !== 'auto'
            || !isNullOrUndefined(this.parent.projectStartDate)) ? false : true;
        this.processTimelineUnit();
        const viewStartAuto = this.parent.timelineSettings.viewStartDate === 'auto';
        const viewEndAuto = this.parent.timelineSettings.viewEndDate === 'auto';
        const projectEndUndefined = isNullOrUndefined(this.parent.projectEndDate);
        if (!viewStartAuto && (!viewEndAuto || (viewEndAuto && projectEndUndefined)) && !this.isZoomToFit) {
            this.parent.updateProjectDates(this.parent.cloneTimelineStartDate, this.parent.cloneTimelineEndDate, this.parent.isTimelineRoundOff);
        }
        else if (viewStartAuto && !this.isZoomToFit && projectEndUndefined) {
            this.parent.updateProjectDates(this.parent.cloneProjectStartDate, this.parent.cloneTimelineEndDate, this.parent.isTimelineRoundOff);
        }
        else if (!viewStartAuto && viewEndAuto && !this.isZoomToFit && !projectEndUndefined) {
            this.parent.updateProjectDates(this.parent.cloneTimelineStartDate, this.parent.cloneProjectEndDate, this.parent.isTimelineRoundOff);
        }
        else {
            this.parent.updateProjectDates(this.parent.cloneProjectStartDate, this.parent.cloneProjectEndDate, this.parent.isTimelineRoundOff);
        }
        const criticalModule = this.parent.criticalPathModule;
        if (this.parent.enableCriticalPath && criticalModule && criticalModule.criticalPathCollection) {
            criticalModule.criticalConnectorLine(criticalModule.criticalPathCollection, criticalModule.detailPredecessorCollection, true, criticalModule.predecessorCollectionTaskIds);
        }
        if (this.isZooming || this.isZoomToFit) {
            const args = {
                requestType: this.isZoomIn ? 'AfterZoomIn' : this.isZoomToFit ? 'AfterZoomToProject' : 'AfterZoomOut',
                timeline: this.parent.currentZoomingLevel
            };
            this.parent.trigger('actionComplete', args);
            this.parent['hideLoadingIndicator']();
        }
        const tier = this.topTier === 'None' ? 'bottomTier' : 'topTier';
        if (this.parent.enableTimelineVirtualization && (!this.parent.pdfExportModule || this.parent.pdfExportModule
            && !this.parent.pdfExportModule.isPdfExport)) {
            this.wholeTimelineWidth = this.calculateWidthBetweenTwoDate(tier, this.parent.timelineModule.timelineStartDate, this.parent.timelineModule.timelineEndDate);
            if (this.wholeTimelineWidth <= this.totalTimelineWidth) {
                this.wholeTimelineWidth = this.totalTimelineWidth;
            }
            // Handled zoomtofit horizontal scrollbar hide while performing different zooming levels in browser at virtualtimeline mode-Task(919516)
            if (this.isZoomToFit) {
                this.clientWidthDifference = Math.abs(this.wholeTimelineWidth - this.parent.element.getElementsByClassName('e-chart-scroll-container e-content')[0].clientWidth) + 1;
                this.parent.element.querySelectorAll('.e-chart-scroll-container')[0].querySelector('.e-virtualtrack')['style'].width = (this.wholeTimelineWidth - this.clientWidthDifference) + 'px';
                if (!isNullOrUndefined(this.parent.element.querySelectorAll('.e-timeline-header-container')[0].querySelector('.e-virtualtrack'))) {
                    this.parent.element.querySelectorAll('.e-timeline-header-container')[0].querySelector('.e-virtualtrack')['style'].width = (this.wholeTimelineWidth - this.clientWidthDifference) + 'px';
                }
            }
            else {
                this.parent.element.querySelectorAll('.e-chart-scroll-container')[0].querySelector('.e-virtualtrack')['style'].width = this.wholeTimelineWidth + 'px';
                if (!isNullOrUndefined(this.parent.element.querySelectorAll('.e-timeline-header-container')[0].querySelector('.e-virtualtrack'))) {
                    this.parent.element.querySelectorAll('.e-timeline-header-container')[0].querySelector('.e-virtualtrack')['style'].width = this.wholeTimelineWidth + 'px';
                }
            }
            this.parent.ganttChartModule.updateWidthAndHeight();
        }
    }
    /**
     * To perform the zoom to fit operation in Gantt.
     *
     * @returns {void}
     * @private
     */
    processZoomToFit() {
        if (this.parent.isReact) {
            this.parent['clearTemplate'](['TaskbarTemplate', 'ParentTaskbarTemplate', 'MilestoneTemplate', 'TaskLabelTemplate', 'RightLabelTemplate', 'LeftLabelTemplate']);
        }
        this.isZoomIn = false;
        this.isZoomToFit = true;
        this.isZooming = false;
        this.isZoomedToFit = true;
        const previousTimeline = {};
        if (this.parent.undoRedoModule && !this.parent.undoRedoModule['isUndoRedoPerformed'] && this.parent['isUndoRedoItemPresent']('ZoomToFit')) {
            if (this.parent.undoRedoModule['redoEnabled']) {
                this.parent.undoRedoModule['disableRedo']();
            }
            this.parent.undoRedoModule['createUndoCollection']();
            previousTimeline['action'] = 'ZoomToFit';
            previousTimeline['previousTimelineStartDate'] = extend([], [], [this.parent.cloneProjectStartDate], true)[0];
            previousTimeline['previousTimelineEndDate'] = extend([], [], [this.parent.cloneProjectEndDate], true)[0];
            previousTimeline['previousZoomingLevel'] = extend({}, {}, this.parent.currentZoomingLevel, true);
            this.parent.undoRedoModule['getUndoCollection'][this.parent.undoRedoModule['getUndoCollection'].length - 1] = previousTimeline;
        }
        if (!this.parent.zoomingProjectStartDate) {
            this.parent.zoomingProjectStartDate = this.parent.cloneProjectStartDate;
            this.parent.zoomingProjectEndDate = this.parent.cloneProjectEndDate;
        }
        if (this.parent.zoomingProjectStartDate > this.parent.cloneProjectStartDate) {
            this.parent.cloneProjectStartDate = new Date(this.parent.allowUnscheduledTasks ?
                this.parent.zoomingProjectStartDate : this.parent.cloneProjectStartDate);
        }
        if (isNullOrUndefined(this.parent.projectStartDate) && isNullOrUndefined(this.parent.projectEndDate)) {
            this.parent.dataOperation.calculateProjectDates();
        }
        let totalDays;
        let nonWorkingDays = 0;
        if (!this.parent.timelineSettings.showWeekend) {
            nonWorkingDays = this.calculateNonWorkingDaysBetweenDates(this.parent.cloneProjectStartDate, this.parent.cloneProjectEndDate);
        }
        const timeDifference = (this.parent.cloneProjectEndDate.getTime() - this.parent.cloneProjectStartDate.getTime());
        totalDays = (timeDifference / (1000 * 3600 * 24));
        totalDays = totalDays - nonWorkingDays;
        const chartWidth = this.parent.ganttChartModule.chartElement.offsetWidth;
        const perDayWidth = chartWidth / totalDays;
        let zoomingLevel;
        let firstValue;
        let secondValue;
        const zoomingCollections = [...this.parent.zoomingLevels];
        const sortedCollectons = zoomingCollections.sort((a, b) => (!a.perDayWidth && !b.perDayWidth ? 0 : (a.perDayWidth < b.perDayWidth) ? 1 : -1));
        if (perDayWidth === 0) { // return when the Gantt chart is not in viewable state.
            return;
        }
        for (let i = 0; i < sortedCollectons.length; i++) {
            firstValue = sortedCollectons[i];
            if (i === sortedCollectons.length - 1) {
                zoomingLevel = sortedCollectons[i];
                break;
            }
            else {
                secondValue = sortedCollectons[i + 1];
            }
            if (perDayWidth >= firstValue.perDayWidth) {
                zoomingLevel = sortedCollectons[i];
                break;
            }
            if (perDayWidth < firstValue.perDayWidth && perDayWidth > secondValue.perDayWidth) {
                zoomingLevel = sortedCollectons[i + 1];
                break;
            }
        }
        const newTimeline = extend({}, {}, zoomingLevel, true);
        if (isNullOrUndefined(this.parent.projectStartDate)) {
            this.roundOffDateToZoom(this.parent.cloneProjectStartDate, true, perDayWidth, newTimeline.bottomTier.unit, zoomingLevel);
        }
        if (isNullOrUndefined(this.parent.projectEndDate)) {
            this.roundOffDateToZoom(this.parent.cloneProjectEndDate, false, perDayWidth, newTimeline.bottomTier.unit, zoomingLevel);
        }
        const numberOfCells = this.calculateNumberOfTimelineCells(newTimeline);
        const scrollHeight = this.parent.ganttChartModule.scrollElement.offsetHeight - 17; //17 is horizontal scrollbar width
        const contentHeight = this.parent.ganttChartModule.chartBodyContent.offsetHeight - 1;
        const emptySpace = contentHeight <= scrollHeight ? 0 : 17;
        newTimeline.timelineUnitSize = Math.abs((chartWidth - emptySpace)) / numberOfCells;
        const args = {
            requestType: 'beforeZoomToProject',
            timeline: newTimeline
        };
        if (this.parent.toolbarModule) {
            this.parent.toolbarModule.enableItems([this.parent.controlId + '_zoomin', this.parent.controlId + '_zoomout'], true);
        }
        this.parent.trigger('actionBegin', args);
        if (!args.cancel) {
            this.parent['showLoadingIndicator']();
            this.changeTimelineSettings(newTimeline);
            // Persist the custom zoom-to-fit timeline so it survives persistence reloads
            this.parent.currentZoomingLevel = extend({}, {}, newTimeline, true);
            this.parent.isTimelineRoundOff = isNullOrUndefined(this.parent.projectStartDate) ? true : false;
        }
        this.isZoomToFit = false;
        this.updateTimelineSettingsAfterZooming();
    }
    bottomTierCellWidthCalc(mode, zoomLevel, date) {
        let convertedMilliSeconds;
        switch (mode) {
            case 'Minutes':
                convertedMilliSeconds = zoomLevel.bottomTier.count * (60 * 1000);
                break;
            case 'Hour':
                convertedMilliSeconds = zoomLevel.bottomTier.count * (60 * 60 * 1000);
                break;
            case 'Week':
                convertedMilliSeconds = zoomLevel.bottomTier.count * (7 * 24 * 60 * 60 * 1000);
                break;
            case 'Day':
                convertedMilliSeconds = zoomLevel.bottomTier.count * (24 * 60 * 60 * 1000);
                break;
            case 'Month':
                {
                    const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
                    convertedMilliSeconds = zoomLevel.bottomTier.count * (60 * 60 * 24 * daysInMonth * 1000);
                    break;
                }
            case 'Year':
                {
                    const daysInYear = (date.getFullYear() % 400 === 0 ||
                        (date.getFullYear() % 100 !== 0 && date.getFullYear() % 4 === 0)) ? 366 : 365;
                    convertedMilliSeconds = zoomLevel.bottomTier.count * (60 * 60 * 24 * daysInYear * 1000);
                    break;
                }
        }
        return convertedMilliSeconds;
    }
    roundOffDateToZoom(date, isStartDate, perDayWidth, tierMode, zoomingLevel) {
        const roundOffTime = this.bottomTierCellWidthCalc(tierMode, zoomingLevel, date);
        if (isStartDate) {
            date.setTime(date.getTime() - roundOffTime);
        }
        else {
            date.setTime(date.getTime() + roundOffTime);
        }
    }
    /**
     * Calculates the number of timeline cells required for a given timeline configuration.
     *
     * @param {ZoomTimelineSettings} newTimeline - The configuration settings for the timeline, including tier settings.
     * @returns {number} - Returns the calculated number of timeline cells based on the unit and count.
     *
     * The method determines the number of days between the project start and end dates, adjusts this value
     * by excluding non-working days if weekends are hidden, and calculates the number of timeline cells
     * that fit within this adjusted duration according to the specified timeline settings.
     */
    calculateNumberOfTimelineCells(newTimeline) {
        const sDate = new Date(this.parent.cloneProjectStartDate.getTime());
        const eDate = new Date(this.parent.cloneProjectEndDate.getTime());
        this.parent.dateValidationModule['updateDateWithTimeZone'](sDate, eDate);
        let numberOfDays;
        let nonWorkingDays = 0;
        if (!this.parent.timelineSettings.showWeekend) {
            nonWorkingDays = this.calculateNonWorkingDaysBetweenDates(this.parent.cloneProjectStartDate, this.parent.cloneProjectEndDate);
        }
        numberOfDays = Math.abs((eDate.getTime() - sDate.getTime()) / (24 * 60 * 60 * 1000));
        numberOfDays -= nonWorkingDays;
        const count = newTimeline.bottomTier.count;
        const unit = newTimeline.bottomTier.unit;
        if (unit === 'Day') {
            return numberOfDays / count;
        }
        else if (unit === 'Week') {
            return (numberOfDays / count) / 7;
        }
        else if (unit === 'Month') {
            return (numberOfDays / count) / 28;
        }
        else if (unit === 'Year') {
            return (numberOfDays / count) / (12 * 28);
        }
        else if (unit === 'Hour') {
            return numberOfDays * (24 / count);
        }
        else {
            return numberOfDays * ((60 * 24) / count);
        }
    }
    /**
     * To validate time line unit.
     *
     * @returns {void}
     * @private
     */
    processTimelineUnit() {
        const directProperty = ['timelineViewMode', 'timelineUnitSize', 'weekStartDay', 'weekendBackground'];
        const innerProperty = {
            'topTier': ['unit', 'format', 'count', 'formatter'],
            'bottomTier': ['unit', 'format', 'count', 'formatter']
        };
        const tierUnits = ['Year', 'Month', 'Week', 'Day', 'Hour', 'Minutes'];
        this.customTimelineSettings = this.customTimelineSettings ? this.customTimelineSettings :
            this.extendFunction(this.parent.timelineSettings, directProperty, innerProperty);
        if ((tierUnits.indexOf(this.customTimelineSettings.topTier.unit) === -1) &&
            (tierUnits.indexOf(this.customTimelineSettings.bottomTier.unit) === -1)) {
            this.customTimelineSettings.topTier.unit = tierUnits.indexOf(this.customTimelineSettings.timelineViewMode) !== -1 ?
                this.customTimelineSettings.timelineViewMode : 'Week';
            this.customTimelineSettings.bottomTier.unit = tierUnits.indexOf(this.customTimelineSettings.topTier.unit) !== 5 ?
                tierUnits[tierUnits.indexOf(this.customTimelineSettings.topTier.unit) + 1] : 'None';
        }
        else if ((tierUnits.indexOf(this.customTimelineSettings.topTier.unit) !== -1 &&
            tierUnits.indexOf(this.customTimelineSettings.bottomTier.unit) !== -1)
            && (tierUnits.indexOf(this.customTimelineSettings.topTier.unit) >
                tierUnits.indexOf(this.customTimelineSettings.bottomTier.unit))) {
            this.customTimelineSettings.bottomTier.unit = this.customTimelineSettings.topTier.unit;
        }
        else {
            this.customTimelineSettings.topTier.unit = tierUnits.indexOf(this.customTimelineSettings.topTier.unit) === -1 ?
                'None' : this.customTimelineSettings.topTier.unit;
            this.customTimelineSettings.bottomTier.unit = tierUnits.indexOf(this.customTimelineSettings.bottomTier.unit) === -1 ?
                'None' : this.customTimelineSettings.bottomTier.unit;
        }
        this.topTier = this.customTimelineSettings.topTier.unit;
        this.bottomTier = this.customTimelineSettings.bottomTier.unit;
        this.previousIsSingleTier = this.isSingleTier;
        this.isSingleTier = this.topTier === 'None' || this.bottomTier === 'None' ? true : false;
    }
    /**
     * To validate timeline properties.
     *
     * @returns {void}
     * @private
     */
    processTimelineProperty() {
        this.customTimelineSettings.topTier.count = (this.topTier === 'None') ?
            1 : this.validateCount(this.customTimelineSettings.topTier.unit, this.customTimelineSettings.topTier.count, 'topTier');
        this.customTimelineSettings.bottomTier.count = this.customTimelineSettings.bottomTier.unit === 'None' ?
            1 : this.validateCount(this.customTimelineSettings.bottomTier.unit, this.customTimelineSettings.bottomTier.count, 'bottomTier');
        this.customTimelineSettings.bottomTier.format = this.validateFormat(this.customTimelineSettings.bottomTier.unit, this.customTimelineSettings.bottomTier.format);
        this.customTimelineSettings.topTier.format = this.validateFormat(this.topTier, this.customTimelineSettings.topTier.format);
        this.customTimelineSettings.weekStartDay = this.customTimelineSettings.weekStartDay >= 0 &&
            this.customTimelineSettings.weekStartDay <= 6 ? this.customTimelineSettings.weekStartDay : 0;
        if (!(this.parent.pdfExportModule && this.parent.pdfExportModule.helper.exportProps &&
            this.parent.pdfExportModule.isPdfExport && this.parent.pdfExportModule.helper.exportProps.fitToWidthSettings.isFitToWidth)) {
            this.checkCurrentZoomingLevel();
        }
    }
    /**
     * To find the current zooming level of the Gantt control.
     *
     * @returns {void}
     * @private
     */
    calculateZoomingLevelsPerDayWidth() {
        const collections = this.parent.zoomingLevels;
        for (let i = 0; i < collections.length; i++) {
            const perDayWidth = this.getPerDayWidth(collections[i].timelineUnitSize, collections[i].bottomTier.count, collections[i].bottomTier.unit);
            collections[i].perDayWidth = perDayWidth;
        }
    }
    /**
     * To find the current zooming level of the Gantt control.
     *
     * @returns {void}
     * @private
     */
    checkCurrentZoomingLevel() {
        const count = this.customTimelineSettings.bottomTier.unit !== 'None' ?
            this.customTimelineSettings.bottomTier.count : this.customTimelineSettings.topTier.count;
        const unit = this.customTimelineSettings.bottomTier.unit !== 'None' ?
            this.customTimelineSettings.bottomTier.unit : this.customTimelineSettings.topTier.unit;
        const tier = this.customTimelineSettings.bottomTier.unit !== 'None' ?
            'bottomTier' : 'topTier';
        let zoomLevel = this.getCurrentZoomingLevel(unit, count, tier);
        const persisted = this.parent.currentZoomingLevel;
        if (persisted && !isNullOrUndefined(persisted.timelineUnitSize)) {
            const matchingIndex = this.parent.zoomingLevels.findIndex((lvl) => {
                return lvl.timelineUnitSize === persisted.timelineUnitSize;
            });
            if (matchingIndex === -1 && this.parent.isLoad) {
                if (!isNullOrUndefined(persisted.level)) {
                    zoomLevel = persisted.level;
                }
            }
            else {
                this.parent.currentZoomingLevel = this.parent.zoomingLevels[zoomLevel];
            }
        }
        else {
            this.parent.currentZoomingLevel = this.parent.zoomingLevels[zoomLevel];
        }
        if (this.parent.toolbarModule) {
            if (zoomLevel === this.parent.zoomingLevels[this.parent.zoomingLevels.length - 1].level) {
                this.parent.toolbarModule.enableItems([this.parent.controlId + '_zoomin'], false);
            }
            else if (zoomLevel === this.parent.zoomingLevels[0].level) {
                this.parent.toolbarModule.enableItems([this.parent.controlId + '_zoomout'], false);
            }
        }
        return zoomLevel;
    }
    /**
     * @param {string} unit .
     * @param {number} count .
     * @param {string} tier .
     * @returns {number} .
     * @private
     */
    getCurrentZoomingLevel(unit, count, tier) {
        let level;
        let currentZoomCollection;
        let checkSameCountLevels;
        let secondValue;
        let firstValue;
        if (!this.parent.zoomingLevels.length) {
            this.parent.zoomingLevels = this.parent.getZoomingLevels();
        }
        let sameUnitLevels = this.parent.zoomingLevels.filter((tempLevel) => {
            if (tier === 'bottomTier') {
                return tempLevel.bottomTier.unit === unit;
            }
            else {
                return tempLevel.topTier.unit === unit;
            }
        });
        if (sameUnitLevels.length === 0) {
            const closestUnit = this.getClosestUnit(unit, '', false);
            sameUnitLevels = this.parent.zoomingLevels.filter((tempLevel) => {
                if (tier === 'bottomTier') {
                    return tempLevel.bottomTier.unit === closestUnit;
                }
                else {
                    return tempLevel.topTier.unit === closestUnit;
                }
            });
        }
        const sortedUnitLevels = sameUnitLevels.sort((a, b) => {
            if (tier === 'bottomTier') {
                return (!a.bottomTier.count || !b.bottomTier.count) ? 0 : ((a.bottomTier.count < b.bottomTier.count) ? 1 : -1);
            }
            else {
                return (!a.topTier.count || !b.topTier.count) ? 0 : ((a.topTier.count < b.topTier.count) ? 1 : -1);
            }
        });
        for (let i = 0; i < sortedUnitLevels.length; i++) {
            firstValue = sortedUnitLevels[i];
            if (i === sortedUnitLevels.length - 1) {
                level = sortedUnitLevels[i].level;
                break;
            }
            else {
                secondValue = sortedUnitLevels[i + 1];
            }
            if (count >= firstValue[`${tier}`].count) {
                currentZoomCollection = sortedUnitLevels[i];
                checkSameCountLevels = sortedUnitLevels.filter((tempLevel) => {
                    if (tier === 'bottomTier') {
                        return tempLevel.bottomTier.count === currentZoomCollection.bottomTier.count;
                    }
                    else {
                        return tempLevel.topTier.count === currentZoomCollection.topTier.count;
                    }
                });
                if (checkSameCountLevels.length > 1) {
                    level = this.checkCollectionsWidth(checkSameCountLevels);
                }
                else {
                    level = checkSameCountLevels[0].level;
                }
                break;
            }
            else if (count < firstValue[`${tier}`].count && count > secondValue[`${tier}`].count) {
                currentZoomCollection = sortedUnitLevels[i + 1];
                checkSameCountLevels = sortedUnitLevels.filter((tempLevel) => {
                    if (tier === 'bottomTier') {
                        return tempLevel.bottomTier.count === currentZoomCollection.bottomTier.count;
                    }
                    else {
                        return tempLevel.topTier.count === currentZoomCollection.topTier.count;
                    }
                });
                if (checkSameCountLevels.length > 1) {
                    level = this.checkCollectionsWidth(checkSameCountLevels);
                }
                else {
                    level = checkSameCountLevels[0].level;
                }
                break;
            }
        }
        return level;
    }
    /**
     * Getting closest zooimg level.
     *
     * @param {string} unit .
     * @param {string} closetUnit .
     * @param {boolean} isCont .
     * @returns {string} .
     * @private
     */
    getClosestUnit(unit, closetUnit, isCont) {
        const bottomTierUnits = ['Year', 'Month', 'Week', 'Day', 'Hour', 'Minutes'];
        const index = bottomTierUnits.indexOf(unit);
        if (index === 0) {
            isCont = true;
        }
        if (this.isZoomIn || isCont) {
            unit = bottomTierUnits[index + 1];
        }
        else {
            unit = bottomTierUnits[index - 1];
        }
        const sameUnitLevels = this.parent.zoomingLevels.filter((tempLevel) => {
            return tempLevel.bottomTier.unit === unit;
        });
        if (sameUnitLevels.length === 0) {
            if (unit === 'Year') {
                isCont = true;
            }
            closetUnit = unit;
            return this.getClosestUnit(unit, closetUnit, isCont);
        }
        else {
            return unit;
        }
    }
    checkCollectionsWidth(checkSameLevels) {
        const zoomLevels = checkSameLevels;
        const width = this.customTimelineSettings.timelineUnitSize;
        let level;
        let secondValue;
        let firstValue;
        const sortedZoomLevels = zoomLevels.sort((a, b) => (a.timelineUnitSize < b.timelineUnitSize) ? 1 : -1);
        for (let i = 0; i < sortedZoomLevels.length; i++) {
            firstValue = sortedZoomLevels[i];
            if (i === sortedZoomLevels.length - 1) {
                level = sortedZoomLevels[i].level;
                break;
            }
            else {
                secondValue = sortedZoomLevels[i + 1];
            }
            if (width >= firstValue.timelineUnitSize) {
                level = sortedZoomLevels[i].level;
                break;
            }
            else if (width < firstValue.timelineUnitSize && width > secondValue.timelineUnitSize) {
                level = sortedZoomLevels[i + 1].level;
                break;
            }
        }
        return level;
    }
    /**
     * To create timeline header template.
     *
     * @returns {void}
     * @private
     */
    updateTimelineHeaderHeight() {
        if (this.parent.timelineModule.isSingleTier) {
            this.parent.element.classList.add(cls.ganttSingleTimeline);
        }
        else {
            this.parent.element.classList.remove(cls.ganttSingleTimeline);
        }
        if (this.previousIsSingleTier !== this.isSingleTier) {
            let toolbarHeight = 0;
            if (!isNullOrUndefined(this.parent.toolbarModule) && !isNullOrUndefined(this.parent.toolbarModule.element)) {
                toolbarHeight = this.parent.toolbarModule.element.offsetHeight;
            }
            this.parent.ganttChartModule.scrollObject.
                setHeight(this.parent.ganttHeight - this.parent.ganttChartModule.chartTimelineContainer.offsetHeight - toolbarHeight);
            this.parent.treeGrid.height = this.parent.ganttHeight - toolbarHeight -
                this.parent.ganttChartModule.chartTimelineContainer.offsetHeight;
        }
    }
    /**
     * Calculates the corresponding date for a given left pixel value on the timeline.
     *
     * @param {number} left - The left position in pixels to be converted to a date.
     * @param {boolean} isMilestone - (Optional) A boolean indicating whether the date refers to a milestone.
     * @param {ITaskData} property - (Optional) An object containing task data, used when adjusting for milestones.
     * @returns {Date} - Returns the calculated date according to the Gantt chart's timeline settings.
     *
     * This method converts a pixel-based position on the Gantt timeline to an actual date,
     * taking into account working days, non-working days, and adjustments for daylight saving
     * time. If weekends are hidden, it calculates the date based on working weeks.
     * For milestones, it adjusts the date by determining the accurate end time.
     */
    dateByLeftValue(left, isMilestone, property) {
        let pStartDate = new Date(this.parent.timelineModule.timelineStartDate.toString());
        const milliSecondsPerPixel = (24 * 60 * 60 * 1000) / this.parent.perDayWidth;
        if (!this.parent.timelineSettings.showWeekend) {
            pStartDate = this.calculateDateExcludingNonWorkingDays(left, pStartDate);
        }
        else {
            pStartDate.setTime(pStartDate.getTime() + (left * milliSecondsPerPixel));
        }
        /* To render the milestone in proper date while editing */
        if (isMilestone && !isNullOrUndefined(property.predecessorsName) && property.predecessorsName !== '') {
            pStartDate.setDate(pStartDate.getDate() - 1);
            const dayEndTime = this.parent['getCurrentDayEndTime'](property.isAutoSchedule ? property.autoEndDate : property.endDate);
            this.parent.dateValidationModule.setTime(dayEndTime, pStartDate);
            pStartDate = this.parent.dateValidationModule.checkStartDate(pStartDate, property, true);
        }
        const tierMode = this.parent.timelineModule.bottomTier !== 'None' ? this.parent.timelineModule.topTier :
            this.parent.timelineModule.bottomTier;
        if (tierMode !== 'Hour' && tierMode !== 'Minutes') {
            if (this.parent.isInDst(new Date(this.parent.timelineModule.timelineStartDate.toString())) &&
                !this.parent.isInDst(pStartDate)) {
                pStartDate.setTime(pStartDate.getTime() + (60 * 60 * 1000));
            }
            else if (!this.parent.isInDst(new Date(this.parent.timelineModule.timelineStartDate.toString())) &&
                this.parent.isInDst(pStartDate)) {
                pStartDate.setTime(pStartDate.getTime() - (60 * 60 * 1000));
            }
        }
        return pStartDate;
    }
    /**
     * Calculates a date by considering a given distance in pixels and excluding non-working days.
     *
     * This function takes into account the width of each day in the Gantt chart as well as non-working days to compute the resulting date from a specified pixel position.
     * It returns the date that corresponds to the pixel distance from `pStartDate`.
     *
     * @param {number} left - The distance in pixels from the start date to calculate the date.
     * @param {Date} pStartDate - The start date from which to calculate the resulting date.
     * @returns {Date} - Returns the calculated date excluding non-working days.
     */
    calculateDateExcludingNonWorkingDays(left, pStartDate) {
        const milliSecondsPerPixel = (24 * 60 * 60 * 1000) / this.parent.perDayWidth;
        const milliSecondsPerDay = (24 * 60 * 60 * 1000);
        const nonWorkingDays = this.parent.nonWorkingDayIndex;
        const totalDays = Math.ceil(left * milliSecondsPerPixel / milliSecondsPerDay);
        const totalDaysPerWeek = 7;
        const workingDaysPerWeek = totalDaysPerWeek - nonWorkingDays.length;
        // Calculate full weeks and extra days
        const fullWeeks = Math.floor(totalDays / workingDaysPerWeek);
        const extraDays = totalDays % workingDaysPerWeek;
        // Set the calculated date by adding full weeks
        const calculatedDate = new Date(pStartDate);
        calculatedDate.setDate(calculatedDate.getDate() + fullWeeks * totalDaysPerWeek);
        // Process the remaining days
        let daysAdded = 0;
        while (daysAdded < extraDays) {
            calculatedDate.setDate(calculatedDate.getDate() + 1);
            if (nonWorkingDays.indexOf(calculatedDate.getDay()) === -1) {
                daysAdded++;
            }
        }
        return calculatedDate;
    }
    /**
     * To create timeline header template.
     *
     * @returns {void}
     * @private
     */
    createTimelineSeries() {
        let tr;
        let td;
        let div;
        let table;
        let thead;
        let virtualTableDiv;
        let virtualTrackDiv;
        const loopCount = this.isSingleTier ? 1 : 2;
        let tier = this.topTier === 'None' ? 'bottomTier' : 'topTier';
        this.topTierCollection = [];
        this.bottomTierCollection = [];
        const isFitToWidthExport = (!this.parent.pdfExportModule || (this.parent.pdfExportModule && !this.parent.pdfExportModule.isPdfExport) ||
            (this.parent.pdfExportModule && this.parent.pdfExportModule.isPdfExport &&
                this.parent.pdfExportModule.helper.exportProps &&
                this.parent.pdfExportModule.helper.exportProps.fitToWidthSettings &&
                !this.parent.pdfExportModule.helper.exportProps.fitToWidthSettings.isFitToWidth));
        if (this.restrictRender === true) {
            this.wholeTimelineWidth = this.calculateWidthBetweenTwoDate(tier, this.parent.timelineModule.timelineStartDate, this.parent.timelineModule.timelineEndDate);
        }
        if (this.parent.enableTimelineVirtualization && (this.wholeTimelineWidth > this.parent.element.offsetWidth * 3)) {
            for (let count = 0; count < loopCount; count++) {
                table = createElement('table', { className: cls.timelineHeaderTableContainer, styles: 'display: block;' });
                table.setAttribute('role', 'none');
                thead = createElement('thead', { className: cls.timelineHeaderTableBody, styles: 'display:block; border-collapse:collapse' });
                const trTemplate = this.createTimelineTemplate(tier);
                tr = createElement('tr');
                if (this.parent.enableHover) {
                    tr.classList.add('e-timeline-cell-hover');
                }
                Array.from(trTemplate.childNodes).forEach((child) => {
                    tr.appendChild(child);
                });
                td = createElement('td');
                div = createElement('div', { styles: 'width: 20px' });
                virtualTableDiv = createElement('div', { className: cls.virtualTable });
                virtualTrackDiv = createElement('div', { className: cls.virtualTrack });
                td.appendChild(div);
                tr.appendChild(td);
                virtualTableDiv.appendChild(tr);
                thead.appendChild(virtualTableDiv);
                thead.appendChild(virtualTrackDiv);
                table.appendChild(thead);
                if (isFitToWidthExport) {
                    this.parent.ganttChartModule.chartTimelineContainer.appendChild(table);
                }
                tier = 'bottomTier';
                tr = null;
                this.restrictRender = false;
            }
            this.updateTimelineHeaderHeight();
            this.timelineVirtualizationStyles();
        }
        else {
            for (let count = 0; count < loopCount; count++) {
                table = createElement('table', { className: cls.timelineHeaderTableContainer, styles: 'display: block;' });
                table.setAttribute('role', 'none');
                thead = createElement('thead', { className: cls.timelineHeaderTableBody, styles: 'display:block; border-collapse:collapse' });
                const trTemplate = this.createTimelineTemplate(tier);
                tr = createElement('tr');
                if (this.parent.enableHover) {
                    tr.classList.add('e-timeline-cell-hover');
                }
                Array.from(trTemplate.childNodes).forEach((child) => {
                    tr.appendChild(child);
                });
                td = createElement('td');
                div = createElement('div', { styles: 'width: 20px' });
                td.appendChild(div);
                tr.appendChild(td);
                thead.appendChild(tr);
                table.appendChild(thead);
                if (isFitToWidthExport) {
                    this.parent.ganttChartModule.chartTimelineContainer.appendChild(table);
                }
                tier = 'bottomTier';
                tr = null;
            }
            this.updateTimelineHeaderHeight();
            this.wholeTimelineWidth = this.totalTimelineWidth;
        }
    }
    timelineVirtualizationStyles() {
        let translateXValue = 0;
        const translateYValue = 0;
        const trackWidth = this.wholeTimelineWidth;
        if (this.parent.enableTimelineVirtualization) {
            //e-content styles updating
            translateXValue = (this.parent.enableTimelineVirtualization &&
                !isNullOrUndefined(this.parent.ganttChartModule.scrollObject.element.scrollLeft)
                && this.parent.ganttChartModule.scrollObject.element.scrollLeft !== 0) ?
                this.parent.ganttChartModule.scrollObject.getTimelineLeft() : 0;
            if (this.parent.enableRtl) {
                translateXValue = -(translateXValue);
            }
            const contentVirtualTable = this.parent.element.querySelectorAll('.e-chart-scroll-container')[0].querySelector('.e-virtualtable');
            if (!this.parent.undoRedoModule || (this.parent.undoRedoModule && !this.parent.undoRedoModule['isZoomingUndoRedoProgress'])) {
                contentVirtualTable.style.transform = `translate3d(${translateXValue}px, ${translateYValue}px, 0px) translateZ(0)`;
            }
            const contentVirtualTrack = this.parent.element.querySelectorAll('.e-chart-scroll-container')[0].querySelector('.e-virtualtrack');
            contentVirtualTrack.style.position = 'relative';
            contentVirtualTrack.style.width = trackWidth + 'px';
            //timeline styles updating
            if (this.parent.ganttChartModule.scrollObject['isSetScrollLeft']) {
                const virtualTableStylesT = this.parent.element.querySelectorAll('.e-timeline-header-table-container')[0].querySelector('.e-virtualtable');
                let virtualTableStylesB;
                if (!isNullOrUndefined(this.parent.element.querySelectorAll('.e-timeline-header-table-container')[1])) {
                    virtualTableStylesB = this.parent.element.querySelectorAll('.e-timeline-header-table-container')[1].querySelector('.e-virtualtable');
                }
                virtualTableStylesT.style.transform = `translate(${translateXValue}px, ${translateYValue}px)`;
                if (!isNullOrUndefined(virtualTableStylesB)) {
                    virtualTableStylesB.style.transform = `translate(${translateXValue}px, ${translateYValue}px)`;
                }
            }
            const virtualTrackStylesT = this.parent.element.querySelectorAll('.e-timeline-header-table-container')[0].querySelector('.e-virtualtrack');
            let virtualTrackStylesB;
            if (!isNullOrUndefined(this.parent.element.querySelectorAll('.e-timeline-header-table-container')[1])) {
                virtualTrackStylesB = this.parent.element.querySelectorAll('.e-timeline-header-table-container')[1].querySelector('.e-virtualtrack');
            }
            if (!isNullOrUndefined(virtualTrackStylesB)) {
                virtualTrackStylesB.style.position = 'relative';
                virtualTrackStylesB.style.width = trackWidth + 'px';
            }
            virtualTrackStylesT.style.position = 'relative';
            virtualTrackStylesT.style.width = trackWidth + 'px';
            //dependency viewer styles updating
            const dependencyViewer = this.parent.connectorLineModule.svgObject;
            dependencyViewer['style'].width = trackWidth + 'px';
            // timeline header container width updating
            const timelineHeader = this.parent.element.querySelector('.' + cls.timelineHeaderContainer);
            timelineHeader['style'].width = 'calc(100% - ' + 17 + 'px)';
            if (this.parent.timelineModule.isZooming || this.parent.timelineModule.isZoomToFit) {
                this.parent.ganttChartModule.scrollElement.scrollLeft = 0;
                this.isZoomingAction = true;
                this.parent.ganttChartModule.scrollObject.updateChartElementStyles();
            }
        }
    }
    /**
     * To validate timeline tier count.
     *
     * @param {string} mode .
     * @param {number} count .
     * @param {string} tier .
     * @returns {number} .
     * @private
     */
    validateCount(mode, count, tier) {
        let tierCount = !isNullOrUndefined(count) && parseInt(count.toString(), 10) > 0 ? parseInt(count.toString(), 10) : 1;
        const timeDifference = Math.abs(this.timelineRoundOffEndDate.getTime() - this.timelineStartDate.getTime());
        let difference;
        switch (mode) {
            case 'Year':
                difference = Math.round((timeDifference / (1000 * 3600 * 24)) / (12 * 28));
                tierCount = tierCount <= difference ? tierCount : difference > 0 ? difference : 1;
                if (this.topTier !== 'None' && tier === 'bottomTier') {
                    tierCount = this.validateBottomTierCount(mode, tierCount);
                }
                break;
            case 'Month':
                difference = Math.round((timeDifference / (1000 * 3600 * 24)) / 28);
                tierCount = tierCount <= difference ? tierCount : difference > 0 ? (difference + 1) : 1;
                if (this.topTier !== 'None' && tier === 'bottomTier') {
                    tierCount = this.validateBottomTierCount(mode, tierCount);
                }
                break;
            case 'Week':
                difference = Math.round((timeDifference / (1000 * 3600 * 24)) / 7);
                tierCount = tierCount <= difference ? tierCount : difference > 0 ? difference : 1;
                if (this.topTier !== 'None' && tier === 'bottomTier') {
                    tierCount = this.validateBottomTierCount(mode, tierCount);
                }
                break;
            case 'Day':
                difference = Math.round(timeDifference / (1000 * 3600 * 24));
                tierCount = tierCount <= difference ? tierCount : difference > 0 ? difference : 1;
                if (this.topTier !== 'None' && tier === 'bottomTier') {
                    tierCount = this.validateBottomTierCount(mode, tierCount);
                }
                break;
            case 'Hour':
                difference = Math.round(timeDifference / (1000 * 3600));
                tierCount = tierCount <= difference ? tierCount : difference > 0 ? difference : 1;
                if (this.topTier !== 'None' && tier === 'bottomTier') {
                    tierCount = this.validateBottomTierCount(mode, tierCount);
                }
                break;
            case 'Minutes':
                difference = Math.round(timeDifference / (1000 * 60));
                tierCount = tierCount <= difference ? tierCount : difference > 0 ? difference : 1;
                if (this.topTier !== 'None' && tier === 'bottomTier') {
                    tierCount = this.validateBottomTierCount(mode, tierCount);
                }
                break;
        }
        if (count !== tierCount && this.isZooming && this.parent.toolbarModule && (tier === 'bottomTier' || this.isSingleTier)) {
            if (this.isZoomIn) {
                this.parent.toolbarModule.enableItems([this.parent.controlId + '_zoomin'], false);
            }
            else {
                this.parent.toolbarModule.enableItems([this.parent.controlId + '_zoomout'], false);
            }
        }
        return tierCount;
    }
    /**
     * To validate bottom tier count.
     *
     * @param {string} mode .
     * @param {number} tierCount .
     * @returns {number} .
     * @private
     */
    validateBottomTierCount(mode, tierCount) {
        let count;
        switch (mode) {
            case 'Year':
                count = tierCount <= this.customTimelineSettings.topTier.count ?
                    tierCount : this.customTimelineSettings.topTier.count;
                break;
            case 'Month':
                count = this.topTier === 'Year' ? tierCount <= (this.customTimelineSettings.topTier.count * 12) ?
                    tierCount : (this.customTimelineSettings.topTier.count * 12) :
                    tierCount <= this.customTimelineSettings.topTier.count ?
                        tierCount : this.customTimelineSettings.topTier.count;
                break;
            case 'Week':
                count = this.topTier === 'Year' ? tierCount <= (this.customTimelineSettings.topTier.count * (12 * 4)) ?
                    tierCount : (this.customTimelineSettings.topTier.count * (12 * 4)) :
                    this.topTier === 'Month' ? tierCount <= (this.customTimelineSettings.topTier.count * 4) ?
                        tierCount : (this.customTimelineSettings.topTier.count * 4) :
                        tierCount <= this.customTimelineSettings.topTier.count ?
                            tierCount : this.customTimelineSettings.topTier.count;
                break;
            case 'Day':
                count = this.topTier === 'Year' ? tierCount <= (this.customTimelineSettings.topTier.count * (12 * 28)) ?
                    tierCount : (this.customTimelineSettings.topTier.count * (12 * 28)) :
                    this.topTier === 'Month' ? tierCount <= (this.customTimelineSettings.topTier.count * 28) ?
                        tierCount : (this.customTimelineSettings.topTier.count * 28) :
                        this.topTier === 'Week' ? tierCount <= (this.customTimelineSettings.topTier.count * 7) ?
                            tierCount : (this.customTimelineSettings.topTier.count * 7) :
                            tierCount <= this.customTimelineSettings.topTier.count ? tierCount : this.customTimelineSettings.topTier.count;
                break;
            case 'Hour':
                count = this.topTier === 'Year' ? tierCount <= (this.customTimelineSettings.topTier.count * (12 * 28 * 24)) ?
                    tierCount : (this.customTimelineSettings.topTier.count * (12 * 28 * 24)) :
                    this.topTier === 'Month' ? tierCount <= (this.customTimelineSettings.topTier.count * (28 * 24)) ?
                        tierCount : (this.customTimelineSettings.topTier.count * (28 * 24)) :
                        this.topTier === 'Week' ? tierCount <= (this.customTimelineSettings.topTier.count * 7 * 24) ?
                            tierCount : (this.customTimelineSettings.topTier.count * 7 * 24) :
                            this.topTier === 'Day' ? tierCount <= (this.customTimelineSettings.topTier.count * 24) ?
                                tierCount : (this.customTimelineSettings.topTier.count * 24) :
                                tierCount <= this.customTimelineSettings.topTier.count ?
                                    tierCount : this.customTimelineSettings.topTier.count;
                break;
            case 'Minutes':
                count = this.topTier === 'Year' ? tierCount <= (this.customTimelineSettings.topTier.count * (12 * 28 * 24 * 60)) ?
                    tierCount : (this.customTimelineSettings.topTier.count * (12 * 28 * 24 * 60)) :
                    this.topTier === 'Month' ? tierCount <= (this.customTimelineSettings.topTier.count * (28 * 24 * 60)) ?
                        tierCount : (this.customTimelineSettings.topTier.count * (28 * 24 * 60)) :
                        this.topTier === 'Week' ? tierCount <= (this.customTimelineSettings.topTier.count * 7 * 24 * 60) ?
                            tierCount : (this.customTimelineSettings.topTier.count * 7 * 24 * 60) :
                            this.topTier === 'Day' ? tierCount <= (this.customTimelineSettings.topTier.count * 24 * 60) ?
                                tierCount : (this.customTimelineSettings.topTier.count * 24 * 60) :
                                this.topTier === 'Hour' ? tierCount <= (this.customTimelineSettings.topTier.count * 60) ?
                                    tierCount : (this.customTimelineSettings.topTier.count * 60) :
                                    tierCount <= this.customTimelineSettings.topTier.count ?
                                        tierCount : this.customTimelineSettings.topTier.count;
                break;
        }
        return count;
    }
    /**
     * To validate timeline tier format.
     *
     * @param {string} mode .
     * @param {string} format .
     * @returns {string} .
     * @private
     */
    validateFormat(mode, format) {
        let tierFormat;
        switch (mode) {
            case 'Week':
                tierFormat = !format ? 'MMM dd, yyyy' : format;
                break;
            case 'Day':
            case 'None':
                tierFormat = !format ? '' : format;
                break;
            case 'Hour':
                tierFormat = !format ? 'H' : format;
                break;
            case 'Month':
                tierFormat = !format ? 'MMM yyyy' : format;
                break;
            case 'Year':
                tierFormat = !format ? 'yyyy' : format;
                break;
            case 'Minutes':
                tierFormat = !format ? 'm' : format;
                break;
        }
        return tierFormat;
    }
    /**
     * To perform extend operation.
     *
     * @param {object} cloneObj .
     * @param {string[]} propertyCollection .
     * @param {object} innerProperty .
     * @returns {object} .
     * @private
     */
    extendFunction(cloneObj, propertyCollection, innerProperty) {
        const tempObj = {};
        for (let index = 0; index < propertyCollection.length; index++) {
            tempObj[propertyCollection[index]] = cloneObj[propertyCollection[index]];
        }
        if (innerProperty) {
            Object.keys(innerProperty).forEach((key) => {
                tempObj[key] = this.extendFunction(cloneObj[key], innerProperty[key], null);
            });
        }
        return tempObj;
    }
    /**
     * To format date.
     *
     * @param {string} dayFormat .
     * @param {Date} data .
     * @param {Date} dummyStartDate .
     * @returns {string} .
     * @private
     */
    formatDateHeader(dayFormat, data, dummyStartDate) {
        const date = new Date(data.getTime());
        let dateString;
        if (dayFormat === '') {
            dateString = this.parent.globalize.formatDate(date, { format: 'E' });
            if (this.parent.locale === 'zh') {
                dateString = dateString.slice(1);
            }
            else {
                if (this.parent.locale === 'ar') {
                    const dateStringValue = dateString;
                    dateString = dateStringValue;
                }
                else {
                    dateString = dateString.slice(0, 1);
                }
            }
        }
        else {
            dateString = this.parent.globalize.formatDate(date, { format: dayFormat });
            if (dummyStartDate) {
                dateString = this.updateHourInFormat(dayFormat, dateString, dummyStartDate.getHours()).updatedFormat;
            }
        }
        return dateString;
    }
    isDateAffectedByDST(modifiedDate) {
        const date = new Date(modifiedDate);
        const offsetAfter = date.getTimezoneOffset();
        for (let hoursBack = 1; hoursBack <= 3; hoursBack++) {
            const testDate = new Date(date.getTime() - hoursBack * 60 * 60 * 1000);
            const offsetBefore = testDate.getTimezoneOffset();
            if (offsetBefore !== offsetAfter) {
                return true; // DST transition happened
            }
        }
        return false; // No DST transition detected
    }
    calculateIteration(dummystartDate, startDate, mode, count) {
        dummystartDate.setHours(startDate.getHours());
        if (this.isFirstLoop && this.parent.isInDst(startDate) &&
            this.isDateAffectedByDST(startDate)) {
            dummystartDate.setHours(dummystartDate.getHours() - 1);
            this.increaseIteration = true;
        }
        this.isFirstLoop = false;
        dummystartDate.setMinutes(startDate.getMinutes());
        dummystartDate.setSeconds(startDate.getSeconds());
        dummystartDate.setMilliseconds(startDate.getMilliseconds());
        let iterations;
        if (mode === 'Hour') {
            const startHour = startDate.getHours();
            // Calculate iterations based on remaining hours in the day
            iterations = Math.ceil((24 - startHour) / count);
        }
        else if (mode === 'Minutes') {
            const startHour = startDate.getHours();
            const startMinute = startDate.getMinutes();
            // Calculate iterations based on remaining minutes in the day
            iterations = Math.ceil((1440 - (startHour * 60 + startMinute)) / count);
        }
        if (this.increaseIteration) {
            iterations += 1;
            this.increaseIteration = false;
        }
        return { iterations: iterations, dummystartDate: dummystartDate };
    }
    updateHourInFormat(dayFormat, formattedDate, newHour) {
        const hour12Pattern = /(h{1,2})/; // Matches 'h' or 'hh'
        const hour24Pattern = /(H{1,2})/; // Matches 'H' or 'HH'
        const hourMatch = dayFormat.match(hour12Pattern) || dayFormat.match(hour24Pattern);
        if (!hourMatch) {
            // No hour format found
            return { hasHour: false, updatedFormat: formattedDate };
        }
        const hourFormat = hourMatch[0]; // Get the hour format string
        const formatParts = dayFormat.split(/[\s,:]+/);
        const dateParts = formattedDate.split(/[\s,:]+/);
        const hourIndex = formatParts.findIndex((part) => part.includes('h') || part.includes('H'));
        if (hourIndex === -1 || hourIndex >= dateParts.length) {
            return { hasHour: false, updatedFormat: formattedDate };
        }
        function padWithZeros(num, length) {
            let numStr = String(num);
            while (numStr.length < length) {
                numStr = '0' + numStr;
            }
            return numStr;
        }
        const formattedHour = padWithZeros(newHour, hourFormat.length);
        dateParts[hourIndex] = formattedHour;
        const updatedDate = dateParts.join(' ');
        return { hasHour: true, hourIndex, updatedFormat: updatedDate };
    }
    /**
     * Custom Formatting.
     *
     * @param {Date} date .
     * @param {string} format .
     * @param {string} tier .
     * @param {string} mode .
     * @param {string | ITimelineFormatter} formatter .
     * @returns {string} .
     * @private
     */
    customFormat(date, format, tier, mode, formatter) {
        formatter = (typeof formatter === 'string' ? getValue(formatter, window) : formatter);
        return formatter(date, format, tier, mode);
    }
    /**
     * To create timeline template .
     *
     * @param {string} tier .
     * @returns {string} .
     * @private
     */
    createTimelineTemplate(tier) {
        let isFirstCell = false;
        const parent = this.parent;
        const parentTh = createElement('th');
        let parentTr;
        const mode = tier === 'topTier' ?
            parent.timelineModule.customTimelineSettings.topTier.unit :
            parent.timelineModule.customTimelineSettings.bottomTier.unit;
        const count = tier === 'topTier' ? parent.timelineModule.customTimelineSettings.topTier.count :
            parent.timelineModule.customTimelineSettings.bottomTier.count;
        const topTier = parent.timelineModule.customTimelineSettings.topTier;
        const bottomTier = parent.timelineModule.customTimelineSettings.bottomTier;
        let increment;
        let newTime;
        const leftValueForStartDate = (this.parent.enableTimelineVirtualization &&
            this.parent.ganttChartModule.scrollObject.element.scrollLeft !== 0)
            ? this.parent.ganttChartModule.scrollObject.getTimelineLeft() : null;
        const startDate = (this.parent.enableTimelineVirtualization && !isNullOrUndefined(leftValueForStartDate))
            ? new Date((this.dateByLeftValue(leftValueForStartDate)).toString()) :
            new Date(this.parent.timelineModule.timelineStartDate.toString());
        const endDate = new Date(this.timelineRoundOffEndDate.toString());
        const scheduleDateCollection = [];
        const uniqueTimestamps = new Set();
        let width = 0;
        const WidthForVirtualTable = this.parent.element.offsetWidth * 3;
        const hasDST = this.parent.dataOperation.hasDSTTransition(startDate.getFullYear());
        let dummystartDate = new Date(2000, 0, 1, 0, 0, 0, 0);
        let loopEnd = false;
        this.isFirstLoop = true;
        do {
            //  to restict the creation of weekend element in UI
            if (!this.parent.timelineSettings.showWeekend && this.isNonWorkingDayHeader(mode, tier, startDate)) {
                increment = Math.abs(this.getIncrement(startDate, count, mode, isFirstCell, true));
                newTime = startDate.getTime() + increment;
                startDate.setTime(newTime);
                continue;
            }
            // PDf export collection
            let timelineCell = {};
            timelineCell.startDate = new Date(startDate.getTime());
            const incrementsDone = {};
            if ((mode === 'Month' || mode === 'Hour') && tier === 'bottomTier' && (count !== 1) && scheduleDateCollection.length === 0) {
                isFirstCell = true;
            }
            if (((mode === 'Hour') || (mode === 'Minutes' && count === 60) ||
                (topTier.unit === 'Hour' && bottomTier.unit === 'Minutes')) && hasDST) {
                const calculatedIteration = this.calculateIteration(dummystartDate, startDate, mode, count);
                const iterations = calculatedIteration.iterations;
                dummystartDate.setTime(calculatedIteration.dummystartDate.getTime());
                for (let i = 0; i < iterations; i++) {
                    timelineCell = {};
                    timelineCell.startDate = new Date(startDate.getTime());
                    if (startDate.getHours() !== dummystartDate.getHours()) {
                        this.applyDstHour = true;
                    }
                    parentTr = this.getHeaterTemplateString(new Date(startDate.toString()), mode, tier, false, count, timelineCell, isFirstCell, dummystartDate);
                    const formattedStartDate = new Date(startDate.toString());
                    const timestamp = formattedStartDate.getTime();
                    if (!uniqueTimestamps.has(timestamp)) {
                        uniqueTimestamps.add(timestamp);
                        scheduleDateCollection.push(formattedStartDate);
                    }
                    const incrementResult = this.getIncrement(startDate, count, mode, isFirstCell, true);
                    let dummyDateIncrement = incrementResult;
                    if (mode === 'Minutes' || mode === 'Hour') {
                        if (this.dstIncreaseHour) {
                            this.fromDummyDate = true;
                            dummyDateIncrement = this.getIncrement(dummystartDate, count, mode, isFirstCell, true);
                            this.dstIncreaseHour = false;
                            this.fromDummyDate = false;
                        }
                        dummystartDate.setTime(dummystartDate.getTime() + dummyDateIncrement);
                        if (this.inconsistenceDstApplied) {
                            startDate.setTime(startDate.getTime() + incrementResult - 1800000);
                            this.inconsistenceDstApplied = false;
                        }
                        else {
                            startDate.setTime(startDate.getTime() + incrementResult);
                        }
                    }
                    isFirstCell = false;
                    if (i === iterations - 1 && startDate.getHours() !== 0) {
                        startDate.setHours(0);
                    }
                    if (startDate.getHours() === 23 && startDate.getHours() !== dummystartDate.getHours()) {
                        startDate.setHours(startDate.getHours() - 1);
                    }
                    if (startDate >= endDate) {
                        /* eslint-disable-next-line */
                        parentTr = this.getHeaterTemplateString(scheduleDateCollection[scheduleDateCollection.length - 1], mode, tier, true, count, timelineCell);
                        loopEnd = true;
                    }
                    parentTh.appendChild(parentTr);
                    const tierCollection = tier === 'topTier' ? this.topTierCollection : this.bottomTierCollection;
                    timelineCell.endDate = new Date(startDate.getTime());
                    if (this.parent.pdfExportModule && this.parent.pdfExportModule.isPdfExport &&
                        this.parent.enableTimelineVirtualization) {
                        if (tier === 'topTier') {
                            this.pdfExportTopTierCollection.push(timelineCell);
                        }
                        else {
                            this.pdfExportBottomTierCollection.push(timelineCell);
                        }
                    }
                    else {
                        tierCollection.push(timelineCell);
                    }
                    width += timelineCell.width;
                    this.weekendEndDate = timelineCell.endDate >= endDate ? endDate : timelineCell.endDate;
                    if (loopEnd) {
                        loopEnd = false;
                        break;
                    }
                }
                this.applyDstHour = false;
                if (mode === 'Hour') {
                    incrementsDone.day = Math.floor((iterations * count) / 24);
                }
                else if (mode === 'Minutes') {
                    incrementsDone.day = Math.floor((iterations * count) / 1440);
                }
                dummystartDate = new Date(2000, 0, 1, 0, 0, 0, 0);
            }
            else {
                parentTr = this.getHeaterTemplateString(new Date(startDate.toString()), mode, tier, false, count, timelineCell, isFirstCell);
                scheduleDateCollection.push(new Date(startDate.toString()));
                if (isFirstCell && mode === 'Month') {
                    newTime = this.calculateQuarterEndDate(startDate, count).getTime();
                }
                else {
                    increment = Math.abs(this.getIncrement(startDate, count, mode, isFirstCell, true));
                    newTime = startDate.getTime() + increment;
                }
                isFirstCell = false;
                if (!this.parent.timelineSettings.showWeekend && mode === 'Day') {
                    let tempCount = 0;
                    const currentDay = new Date(startDate);
                    // Adjust the start date to find the next working day
                    while (tempCount < count) {
                        currentDay.setHours(0, 0, 0, 0);
                        currentDay.setDate(currentDay.getDate() + 1);
                        // Check if the current day is a working day
                        if (this.parent.nonWorkingDayIndex.indexOf(currentDay.getDay()) === -1) {
                            // Increment tempCount if it's a working day
                            tempCount++;
                        }
                    }
                    newTime = currentDay.getTime();
                }
                startDate.setTime(newTime);
                if ((mode === 'Day' || mode === 'Month' || mode === 'Week') && hasDST) {
                    startDate.setHours(0, 0, 0, 0);
                }
                if (startDate >= endDate) {
                    /* eslint-disable-next-line */
                    parentTr = this.getHeaterTemplateString(scheduleDateCollection[scheduleDateCollection.length - 1], mode, tier, true, count, timelineCell);
                }
                parentTh.appendChild(parentTr);
                const tierCollection = tier === 'topTier' ? this.topTierCollection : this.bottomTierCollection;
                timelineCell.endDate = new Date(startDate.getTime());
                if (this.parent.pdfExportModule && this.parent.pdfExportModule.isPdfExport && this.parent.enableTimelineVirtualization) {
                    if (tier === 'topTier') {
                        this.pdfExportTopTierCollection.push(timelineCell);
                    }
                    else {
                        this.pdfExportBottomTierCollection.push(timelineCell);
                    }
                }
                else {
                    tierCollection.push(timelineCell);
                }
                width += timelineCell.width;
                this.weekendEndDate = timelineCell.endDate >= endDate ? endDate : timelineCell.endDate;
            }
        } while ((this.parent.enableTimelineVirtualization && (!this.parent.pdfExportModule ||
            this.parent.pdfExportModule && !this.parent.pdfExportModule.isPdfExport)) ? (width < WidthForVirtualTable) &&
            (startDate < endDate) : (startDate < endDate));
        return parentTh;
    }
    isNonWorkingDayHeader(mode, tier, day) {
        return (mode === 'Day' || mode === 'Hour' || mode === 'Minutes') &&
            this.parent.nonWorkingDayIndex.indexOf(day.getDay()) !== -1;
    }
    updateTimelineAfterZooming(endDate, resized) {
        let timeDiff;
        let perDayWidth;
        let totWidth;
        let contentWidth;
        const contentElement = document.getElementsByClassName('e-chart-scroll-container e-content')[0];
        if (!isNullOrUndefined(contentElement) && !(this.parent.pdfExportModule && this.parent.pdfExportModule.isPdfExport)) {
            if (!this.parent.isLoad && this.parent.splitterModule && this.parent.splitterModule.splitterObject &&
                this.parent.splitterSettings.view === 'Chart') {
                contentWidth = contentElement['offsetWidth'] + this.parent.splitterModule.splitterObject['allPanes'][0].offsetWidth;
            }
            else {
                contentWidth = contentElement['offsetWidth'];
            }
            const contentHeight = contentElement['offsetHeight'];
            const scrollHeight = document.getElementsByClassName('e-chart-rows-container')[0]['offsetHeight'];
            timeDiff = Math.abs(this.timelineStartDate.getTime() - endDate.getTime());
            timeDiff = timeDiff / (1000 * 3600 * 24);
            if (!this.parent.timelineSettings.showWeekend) {
                const nonWorking = this.calculateNonWorkingDaysBetweenDates(this.timelineStartDate, endDate);
                timeDiff = timeDiff - nonWorking;
            }
            if (this.bottomTier === 'None') {
                perDayWidth = this.getPerDayWidth(this.customTimelineSettings.timelineUnitSize, this.customTimelineSettings.topTier.count, this.topTier);
            }
            else {
                perDayWidth = this.getPerDayWidth(this.customTimelineSettings.timelineUnitSize, this.customTimelineSettings.bottomTier.count, this.bottomTier);
            }
            if (contentHeight < scrollHeight) {
                totWidth = (perDayWidth * timeDiff) + 17;
            }
            else {
                totWidth = (perDayWidth * timeDiff);
            }
            if (contentWidth >= totWidth) {
                let widthDiff = contentWidth - totWidth;
                if (!this.parent.timelineSettings.showWeekend) {
                    // Calculate extra working days needed based on remaining pixel width
                    const extraPixels = widthDiff;
                    const extraWorkingDays = Math.ceil(extraPixels / perDayWidth);
                    // Add working days to endDate skipping non-working day indices
                    let daysToAdd = 0;
                    let addedWorkingDays = 0;
                    const nonWorkingIdx = this.parent.nonWorkingDayIndex;
                    while (addedWorkingDays < extraWorkingDays) {
                        daysToAdd++;
                        const tempDate = new Date(endDate.getTime());
                        tempDate.setDate(tempDate.getDate() + daysToAdd);
                        if (nonWorkingIdx.indexOf(tempDate.getDay()) === -1) {
                            addedWorkingDays++;
                        }
                    }
                    endDate.setDate(endDate.getDate() + daysToAdd);
                }
                else {
                    widthDiff = Math.round(widthDiff / perDayWidth);
                    endDate.setDate(endDate.getDate() + widthDiff);
                }
                this.parent.timelineModule.timelineEndDate = endDate;
                if (resized) {
                    this.parent.updateProjectDates(this.timelineStartDate, this.timelineEndDate, this.parent.isTimelineRoundOff);
                }
            }
        }
    }
    getTimelineRoundOffEndDate(date) {
        const tierMode = this.topTier === 'None' ? this.bottomTier : this.topTier;
        const endDate = new Date(date.toString());
        if (this.parent.isTimelineRoundOff) {
            if (tierMode === 'Hour') {
                endDate.setMinutes(60);
            }
            else if (tierMode === 'Minutes') {
                endDate.setSeconds(60);
            }
            else {
                endDate.setHours(24, 0, 0, 0);
            }
        }
        if (isNullOrUndefined(this.parent.projectEndDate) && this.parent.timelineSettings.viewEndDate === 'auto') {
            this.updateTimelineAfterZooming(endDate, false);
        }
        return endDate;
    }
    /**
     *
     * @param {Date} startDate .
     * @param {number} count .
     * @param {string} mode .
     * @param {boolean} [isFirstCell] .
     * @param {boolean} [dateIncrement] .
     * @returns {number} .
     * @private
     */
    getIncrement(startDate, count, mode, isFirstCell, dateIncrement) {
        let firstDay = new Date(startDate.getTime());
        let lastDay = new Date(startDate.getTime());
        let increment;
        let isDstEnd = false;
        const dstDateCompate = new Date(firstDay);
        switch (mode) {
            case 'Year': {
                firstDay = startDate;
                lastDay = new Date(startDate.getFullYear() + (count - 1), 11, 31);
                const startDateUTC = getUniversalTime(startDate);
                const endDateUTC = this.resetToNextYear(startDate, count);
                const expectedYearDiff = endDateUTC - startDateUTC;
                increment = this.adjustForDST(firstDay, lastDay, expectedYearDiff, (lastDay.getTime() - firstDay.getTime()) + (1000 * 60 * 60 * 24), dateIncrement, true);
                break;
            }
            case 'Month': {
                firstDay = startDate;
                lastDay = new Date(startDate.getFullYear(), startDate.getMonth() + count, 1);
                const startDateUTC = getUniversalTime(startDate);
                const endDateUTC = this.resetToNextMonth(startDate, count);
                const expectedMonthDiff = endDateUTC - startDateUTC;
                increment = this.adjustForDST(firstDay, lastDay, expectedMonthDiff, lastDay.getTime() - firstDay.getTime(), dateIncrement, true);
                break;
            }
            case 'Week': {
                const dayIndex = this.parent.timelineModule.customTimelineSettings.weekStartDay;
                let dayIntervel = startDate.getDay() < dayIndex ? (dayIndex - startDate.getDay()) :
                    (6 - startDate.getDay()) + dayIndex;
                count = dayIntervel > 0 ? count - 1 : 0;
                lastDay.setHours(24, 0, 0, 0);
                dayIntervel = startDate.getDay() < dayIndex ? dayIntervel > 0 ?
                    dayIntervel - 1 : dayIntervel : dayIntervel;
                lastDay.setDate(lastDay.getDate() + (dayIntervel + (7 * count)));
                const nextDayMidnightUTC = this.resetToNextDay(startDate);
                const startDateUTC = getUniversalTime(startDate);
                const endDateUTC = nextDayMidnightUTC + ((dayIntervel + (7 * count)) * 24 * 60 * 60 * 1000);
                const expectedWeekDiff = endDateUTC - startDateUTC;
                increment = this.adjustForDST(firstDay, lastDay, expectedWeekDiff, lastDay.getTime() - firstDay.getTime(), dateIncrement, true);
                break;
            }
            case 'Day': {
                lastDay.setHours(24, 0, 0, 0);
                const nextDayMidnightUTC = this.resetToNextDay(firstDay);
                const startDateUTC = getUniversalTime(firstDay);
                const endDateUTC = nextDayMidnightUTC + ((count - 1) * 24 * 60 * 60 * 1000);
                const expectedDayDiff = endDateUTC - startDateUTC;
                increment = (lastDay.getTime() - firstDay.getTime()) + (1000 * 60 * 60 * 24 * (count - 1));
                const date = new Date(firstDay);
                date.setTime(date.getTime() + increment);
                increment = this.adjustForDST(firstDay, date, expectedDayDiff, date.getTime() - firstDay.getTime(), dateIncrement, true);
                break;
            }
            case 'Hour': {
                lastDay.setMinutes(60);
                lastDay.setSeconds(0);
                const nextHourUtC = this.resetToNextHour(firstDay);
                const startDateUTC = getUniversalTime(firstDay);
                const endDateUTC = nextHourUtC + ((count - 1) * 60 * 60 * 1000);
                const expectedHourDiff = endDateUTC - startDateUTC;
                increment = (lastDay.getTime() - firstDay.getTime()) + (1000 * 60 * 60 * (count - 1));
                const date = new Date(firstDay);
                date.setTime(date.getTime() + increment);
                if (isFirstCell && count === 12) {
                    if (firstDay.getHours() !== 0) {
                        date.setHours(0, 0, 0, 0);
                    }
                }
                const dstDateCompare = new Date(firstDay);
                dstDateCompare.setHours(dstDateCompare.getHours() - count);
                const offsetDiff = firstDay.getTimezoneOffset() - dstDateCompare.getTimezoneOffset();
                if (offsetDiff === -30) {
                    date.setMinutes(date.getMinutes() + 30);
                    this.inconsistenceDstApplied = true;
                }
                if (date.getHours() < (dstDateCompate.getHours() + count)) {
                    isDstEnd = true;
                }
                increment = this.adjustForDST(firstDay, date, expectedHourDiff, date.getTime() - firstDay.getTime(), dateIncrement, isDstEnd);
                break;
            }
            case 'Minutes': {
                lastDay.setSeconds(60);
                const nextMinuteUtC = this.resetToNextMinute(firstDay);
                const startDateUTC = getUniversalTime(firstDay);
                const endDateUTC = nextMinuteUtC + ((count - 1) * 60 * 1000);
                const expectedMinuteDiff = endDateUTC - startDateUTC;
                increment = (lastDay.getTime() - firstDay.getTime()) + (1000 * 60 * (count - 1));
                const date = new Date(firstDay);
                date.setTime(date.getTime() + increment);
                if (date.getMinutes() <= dstDateCompate.getMinutes()) {
                    isDstEnd = true;
                }
                increment = this.adjustForDST(firstDay, date, expectedMinuteDiff, date.getTime() - firstDay.getTime(), dateIncrement, isDstEnd);
                break;
            }
        }
        return increment;
    }
    resetToNextYear(now, count) {
        const year = now.getFullYear() + (count);
        return Date.UTC(year, 0, 1, 0, 0, 0, 0);
    }
    resetToNextMonth(now, count) {
        const year = now.getFullYear();
        const month = now.getMonth();
        return Date.UTC(year, month + count, 1, 0, 0, 0, 0); // First day of the next month at midnight
    }
    resetToNextDay(now) {
        const year = now.getFullYear();
        const month = now.getMonth();
        const day = now.getDate();
        return Date.UTC(year, month, day + 1, 0, 0, 0, 0); // Midnight of the next day
    }
    resetToNextHour(now) {
        const year = now.getFullYear();
        const month = now.getMonth();
        const day = now.getDate();
        const hours = now.getHours();
        return Date.UTC(year, month, day, hours + 1, 0, 0, 0); // Next hour with 0 minutes, seconds, and ms
    }
    resetToNextMinute(now) {
        const year = now.getFullYear();
        const month = now.getMonth();
        const day = now.getDate();
        const hours = now.getHours();
        const minutes = now.getMinutes();
        return Date.UTC(year, month, day, hours, minutes + 1, 0, 0); // Next minute with 0 seconds and ms
    }
    adjustForDST(firstDay, lastDay, expectedDiff, increment, dateIncrement, isDstEnd) {
        if (!this.parent.dataOperation.hasDSTTransition(firstDay.getFullYear())) {
            return increment;
        }
        if (this.inconsistenceDstApplied) {
            return increment;
        }
        const dstAdjustment = increment - expectedDiff;
        if (lastDay.getTimezoneOffset() > firstDay.getTimezoneOffset() && dateIncrement) {
            this.dstIncreaseHour = true;
            if (this.fromDummyDate) {
                return expectedDiff;
            }
            return isDstEnd ? increment + (60 * 60 * 1000) : increment; // Add one hour for DST transition (spring forward);
        }
        if (dstAdjustment > 0) {
            increment -= dstAdjustment;
        }
        else if (dstAdjustment < 0) {
            increment += Math.abs(dstAdjustment);
        }
        if (expectedDiff - increment === 0) {
            return increment;
        }
        return increment;
    }
    /**
     * Method to find header cell was weekend or not
     *
     * @param {string} mode .
     * @param {string} tier .
     * @param {Date} day .
     * @returns {boolean} .
     */
    isWeekendHeaderCell(mode, tier, day) {
        return (mode === 'Day' || mode === 'Hour' || mode === 'Minutes') && (this.customTimelineSettings[tier].count === 1 ||
            mode === 'Hour' || mode === 'Minutes') &&
            this.parent.nonWorkingDayIndex.indexOf(day.getDay()) !== -1;
    }
    calculateQuarterEndDate(date, count) {
        const month = date.getMonth();
        if (count === 3) {
            if (month >= 0 && month <= 2) {
                return new Date(date.getFullYear(), 3, 1);
            }
            else if (month >= 3 && month <= 5) {
                return new Date(date.getFullYear(), 6, 1);
            }
            else if (month >= 6 && month <= 8) {
                return new Date(date.getFullYear(), 9, 1);
            }
            else {
                return new Date(date.getFullYear() + 1, 0, 1);
            }
        }
        else {
            if (month >= 0 && month <= 5) {
                return new Date(date.getFullYear(), 6, 1);
            }
            else {
                return new Date(date.getFullYear() + 1, 0, 1);
            }
        }
    }
    calculateTotalHours(mode, count) {
        let totalHour = 0;
        switch (mode) {
            case 'Hour':
                totalHour = 1 * count;
                break;
            case 'Day':
                totalHour = 24 * count;
                break;
            case 'Week':
                totalHour = 7 * 24 * count;
                break;
            case 'Minutes':
                totalHour = count / 60;
                break;
        }
        return totalHour;
    }
    /**
     * To construct template string.
     *
     * @param {Date} scheduleWeeks .
     * @param {string} mode .
     * @param {string} tier .
     * @param {boolean} isLast .
     * @param {number} count .
     * @param {TimelineFormat} timelineCell .
     * @returns {string} .
     * @private
     */
    /* eslint-disable-next-line */
    getHeaterTemplateString(scheduleWeeks, mode, tier, isLast, count, timelineCell, isFirstCell, dummystartDate) {
        let parentTr = '';
        let template;
        let timelineTemplate = null;
        if (!isNullOrUndefined(this.parent.timelineTemplate)) {
            timelineTemplate = this.parent.chartRowsModule.templateCompiler(this.parent.timelineTemplate);
        }
        const format = tier === 'topTier' ?
            this.parent.timelineModule.customTimelineSettings.topTier.format :
            this.parent.timelineModule.customTimelineSettings.bottomTier.format;
        const formatter = tier === 'topTier' ?
            this.parent.timelineModule.customTimelineSettings.topTier.formatter :
            this.parent.timelineModule.customTimelineSettings.bottomTier.formatter;
        let thWidth;
        let increment = this.getIncrement(scheduleWeeks, count, mode, isFirstCell);
        if (!this.parent.timelineSettings.showWeekend && (((mode === 'Week' || mode === 'Month' || mode === 'Year') && tier === 'topTier' &&
            this.parent.currentZoomingLevel.bottomTier.unit === 'Day' || this.parent.currentZoomingLevel.bottomTier.unit === 'Hour') || this.isZoomToFit)) {
            const copyStartDate = new Date(scheduleWeeks);
            const orginalDate = new Date(scheduleWeeks);
            const enddate = new Date(orginalDate.getTime() + increment);
            enddate.setHours(0, 0, 0, 0);
            const nonWorkingDay = this.calculateNonWorkingDaysBetweenDates(copyStartDate, enddate);
            increment = increment - (nonWorkingDay * 24 * 60 * 60 * 1000);
        }
        thWidth = Math.abs((increment / (1000 * 60 * 60 * 24)) * this.parent.perDayWidth);
        const cellWidth = thWidth;
        thWidth = isLast
            ? this.calculateWidthBetweenTwoDate(mode, scheduleWeeks, this.timelineRoundOffEndDate, timelineCell, isLast)
            : (isFirstCell && mode !== 'Hour')
                ? this.calculateWidthBetweenTwoDate(mode, scheduleWeeks, this.calculateQuarterEndDate(scheduleWeeks, count))
                : thWidth;
        if (this.isFirstLoop && this.parent.isInDst(scheduleWeeks) && scheduleWeeks.getHours() === 1 &&
            this.isDateAffectedByDST(scheduleWeeks)) {
            thWidth += this.parent.perDayWidth / 24;
        }
        this.isFirstLoop = false;
        const isWeekendCell = this.isWeekendHeaderCell(mode, tier, scheduleWeeks);
        const textClassName = tier === 'topTier' ? ' e-gantt-top-cell-text' : '';
        if (isFirstCell && scheduleWeeks.getHours() === 20 && count === 12 && tier === 'bottomTier' &&
            this.parent.timelineModule.customTimelineSettings.bottomTier.unit === 'Hour') {
            scheduleWeeks.setTime(scheduleWeeks.getTime() - (1000 * 60 * 60 * 20));
        }
        let displayDate = new Date(scheduleWeeks);
        if (!this.parent.timelineSettings.showWeekend) {
            const isWeekend = this.isWeekend(displayDate);
            if (isWeekend) {
                while (this.parent.nonWorkingDayIndex.indexOf(displayDate.getDay()) !== -1) {
                    displayDate.setDate(displayDate.getDate() + 1);
                }
            }
        }
        else {
            displayDate = new Date(scheduleWeeks);
        }
        const date = isNullOrUndefined(formatter) ?
            this.parent.globalize.formatDate(displayDate, { format: this.parent.getDateFormat() }) :
            this.customFormat(displayDate, format, tier, mode, formatter);
        let value;
        if (this.applyDstHour) {
            value = (isNullOrUndefined(formatter) ? this.formatDateHeader(format, displayDate, dummystartDate) :
                this.customFormat(displayDate, format, tier, mode, formatter));
        }
        else {
            value = (isNullOrUndefined(formatter) ? this.formatDateHeader(format, displayDate) :
                this.customFormat(displayDate, format, tier, mode, formatter));
        }
        if (!isNullOrUndefined(timelineTemplate)) {
            const args = {
                date: date,
                tier: tier,
                value: value
            };
            template = timelineTemplate(extend({}, args), this.parent, 'TimelineTemplate', this.parent.chartRowsModule.getTemplateID('TimelineTemplate'), false, undefined, null, this.parent['root']);
            const firstElement = template[0];
            if (firstElement instanceof HTMLElement) {
                firstElement.setAttribute('data-tier', tier);
                firstElement.setAttribute('date', date);
                firstElement.setAttribute('value', value);
            }
        }
        const className = this.parent.timelineModule.isSingleTier ? cls.timelineSingleHeaderCell : cls.timelineTopHeaderCell;
        const weekendClass = isWeekendCell ? ' ' + cls.weekendHeaderCell : '';
        const th = createElement('th', {
            className: `${className}${weekendClass}`,
            styles: `width:${thWidth}px;${isWeekendCell && this.customTimelineSettings.weekendBackground ? 'background-color:' + this.customTimelineSettings.weekendBackground + ';' : ''}`
        });
        if (isWeekendCell && this.parent.enableHover) {
            th.classList.add('e-weekend-cell-hover');
        }
        th.tabIndex = -1;
        th.setAttribute('aria-label', `${this.parent.localeObj.getConstant('timelineCell')} ${date}`);
        const div = createElement('div', {
            className: `${cls.timelineHeaderCellLabel}${textClassName}`,
            styles: `width:${thWidth - 1}px;`
        });
        div.title = this.parent.timelineSettings.showTooltip ? date : '';
        div.textContent = value;
        div.setAttribute('data-tier', tier);
        if (this.parent.isReact && !isNullOrUndefined(template) && template.length > 0) {
            template[0]['style'].width = '100%';
            template[0]['style'].height = '100%';
        }
        if (!isNullOrUndefined(timelineTemplate) && !isNullOrUndefined(template) && template.length > 0) {
            append(template, th);
        }
        else {
            th.append(div);
        }
        /* eslint-disable-next-line */
        parentTr += th.outerHTML;
        if ((this.isSingleTier || tier === 'topTier') && !isLast) {
            this.totalTimelineWidth = this.totalTimelineWidth + thWidth;
        }
        else if ((this.isSingleTier || tier === 'topTier') && isLast) {
            this.totalTimelineWidth = (this.totalTimelineWidth - cellWidth) + thWidth;
        }
        // PDf export collection
        timelineCell.value = value;
        timelineCell.isWeekend = isWeekendCell;
        timelineCell.width = thWidth;
        return th;
    }
    /**
     * Calculates the total number of non-working days between two given dates.
     *
     * @param {Date} startDate - The start date of the period to check for non-working days.
     * @param {Date} endDate - The end date of the period to check for non-working days.
     * @returns {number} - Returns the total count of non-working days between the specified dates.
     *
     * This method takes into account complete weeks and any additional days, calculating
     * non-working days within complete weeks based on the known non-working day indices.
     * It iterates through any extra days beyond complete weeks to check if they are non-working.
     */
    calculateNonWorkingDaysBetweenDates(startDate, endDate) {
        const MS_PER_DAY = 1000 * 60 * 60 * 24;
        // Calculate the total number of days
        const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / MS_PER_DAY);
        // Calculate complete weeks and extra days
        const completeWeeks = Math.floor(totalDays / 7);
        const extraDays = totalDays % 7;
        // Calculate non-working days in complete weeks
        const nonWorkingDaysPerWeek = this.parent.nonWorkingDayIndex.length;
        let nonWorkingDaysCount = completeWeeks * nonWorkingDaysPerWeek;
        // Calculate the start day and remaining days after complete weeks
        const startDay = startDate.getDay();
        for (let i = 0; i < extraDays; i++) {
            const currentDay = (startDay + i) % 7;
            if (this.parent.nonWorkingDayIndex.indexOf(currentDay) !== -1) {
                nonWorkingDaysCount++;
            }
        }
        return nonWorkingDaysCount;
    }
    /**
     * Determines if a given date is a weekend or a non-working day.
     *
     * @param {Date} date - The date to check.
     * @returns {boolean} - Returns `true` if the specified date is a non-working day, otherwise `false`.
     *
     * This method checks if the day of the given date falls within the defined non-working days.
     * The non-working days are identified using the `nonWorkingDayIndex` from the parent configuration.
     */
    isWeekend(date) {
        const day = date.getDay();
        return this.parent.nonWorkingDayIndex.indexOf(day) !== -1;
    }
    /**
     * To calculate last 'th' width.
     *
     * @param {string} mode .
     * @param {Date} scheduleWeeks .
     * @param {Date} endDate .
     * @param {TimelineFormat} timelineCell .
     * @param {boolean} isLast .
     * @returns {number} .
     * @private
     */
    /* eslint-disable-next-line */
    calculateWidthBetweenTwoDate(mode, scheduleWeeks, endDate, timelineCell, isLast) {
        const sDate = new Date(scheduleWeeks.getTime());
        const eDate = new Date(endDate.getTime());
        this.parent.dateValidationModule['updateDateWithTimeZone'](sDate, eDate);
        const timeDifference = (eDate.getTime() - sDate.getTime());
        const totalDays = timeDifference / (1000 * 60 * 60 * 24);
        if (!this.parent.timelineSettings.showWeekend && (this.parent.currentZoomingLevel.bottomTier.unit === 'Day' ||
            this.parent.currentZoomingLevel.bottomTier.unit === 'Hour' || this.isZoomToFit)) {
            const totalWeeks = Math.floor(totalDays / 7);
            const completeWeekDays = totalWeeks * (7 - this.parent.nonWorkingDayIndex.length);
            const remainingDays = totalDays % 7;
            if (remainingDays > 1 || completeWeekDays > 1) {
                let additionalWorkingDays = 0;
                const currentDate = new Date(sDate);
                for (let i = 0; i < remainingDays; i++) {
                    const dayOfWeek = currentDate.getDay();
                    if (this.parent.nonWorkingDayIndex.indexOf(dayOfWeek) === -1) {
                        additionalWorkingDays++;
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                }
                const workingDays = completeWeekDays + additionalWorkingDays;
                return workingDays * this.parent.perDayWidth;
            }
            else if (this.isZoomToFit && isLast) {
                return timelineCell.width;
            }
            else {
                return totalDays * this.parent.perDayWidth;
            }
        }
        else {
            return totalDays * this.parent.perDayWidth;
        }
    }
    /**
     * To calculate timeline width.
     *
     * @returns {void} .
     * @private
     */
    timelineWidthCalculation() {
        const timelineUnitSize = this.customTimelineSettings.timelineUnitSize;
        const bottomTierCount = this.customTimelineSettings.bottomTier.count;
        const topTierCount = this.customTimelineSettings.topTier.count;
        this.bottomTierCellWidth = timelineUnitSize;
        if (this.bottomTier === 'None') {
            this.parent.perDayWidth = this.getPerDayWidth(timelineUnitSize, topTierCount, this.topTier);
        }
        else {
            this.parent.perDayWidth = this.getPerDayWidth(timelineUnitSize, bottomTierCount, this.bottomTier);
        }
        this.topTierCellWidth = this.bottomTier !== 'None' ? this.topTier === 'Week' ?
            this.parent.perDayWidth * 7 : this.topTier === 'Hour' ?
            this.parent.perDayWidth / 24 : this.topTier === 'Minutes' ?
            this.parent.perDayWidth / (24 * 60) : this.parent.perDayWidth : timelineUnitSize;
        this.topTierCellWidth = this.isSingleTier ? this.topTierCellWidth : this.topTierCellWidth * topTierCount;
    }
    /**
     * To validate per day width.
     *
     * @param {number} timelineUnitSize .
     * @param {number} bottomTierCount .
     * @param {string} mode .
     * @returns {number} .
     * @private
     */
    getPerDayWidth(timelineUnitSize, bottomTierCount, mode) {
        let perDayWidth;
        switch (mode) {
            case 'Year':
                perDayWidth = (timelineUnitSize / bottomTierCount) / (12 * 28);
                break;
            case 'Month':
                perDayWidth = (timelineUnitSize / bottomTierCount) / 28;
                break;
            case 'Week':
                perDayWidth = (timelineUnitSize / bottomTierCount) / 7;
                break;
            case 'Day':
                perDayWidth = timelineUnitSize / bottomTierCount;
                break;
            case 'Hour':
                perDayWidth = (24 / bottomTierCount) * timelineUnitSize;
                break;
            case 'Minutes':
                perDayWidth = ((60 * 24) / bottomTierCount) * timelineUnitSize;
                break;
        }
        return perDayWidth;
    }
    /**
     * To validate project start date and end date.
     *
     * @returns {void} .
     * @private
     */
    roundOffDays() {
        let startDate = this.parent.timelineSettings.viewStartDate !== 'auto' && !this.isZoomToFit ? this.parent.cloneTimelineStartDate :
            this.parent.cloneProjectStartDate;
        let endDate = (((this.parent.timelineSettings.viewEndDate !== 'auto' || isNullOrUndefined(this.parent.projectEndDate))
            || (this.parent.timelineSettings.viewStartDate !== 'auto' && this.parent.timelineSettings.viewEndDate === 'auto')) && !this.isZoomToFit) ?
            this.parent.cloneTimelineEndDate : this.parent.cloneProjectEndDate;
        const tierMode = this.topTier === 'None' ? this.bottomTier : this.topTier;
        if (this.parent.isTimelineRoundOff) {
            if (tierMode === 'Year') {
                startDate = new Date(startDate.getFullYear(), 0, 1);
                endDate = new Date(endDate.getFullYear(), 11, 31);
            }
            else if (tierMode === 'Month') {
                startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
                endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);
            }
            else if (tierMode === 'Week') {
                const dayIndex = !isNullOrUndefined(this.customTimelineSettings.weekStartDay) ?
                    this.parent.timelineModule.customTimelineSettings.weekStartDay : 0;
                let roundOffStartDate = startDate.getDay() < dayIndex ?
                    (startDate.getDate()) - (7 - dayIndex + startDate.getDay()) :
                    (startDate.getDate()) - startDate.getDay() + dayIndex;
                if (!this.parent.timelineSettings.showWeekend) {
                    while (this.isWeekend(new Date(startDate.getFullYear(), startDate.getMonth(), roundOffStartDate))) {
                        roundOffStartDate++;
                    }
                }
                startDate.setDate(roundOffStartDate);
                const first = endDate.getDate() - endDate.getDay();
                const last = first + 6 + dayIndex;
                endDate.setDate(last);
            }
            if (tierMode === 'Hour') {
                startDate.setMinutes(0);
            }
            else if (tierMode === 'Minutes') {
                startDate.setSeconds(0);
            }
            else {
                startDate.setHours(0, 0, 0, 0);
            }
        }
        this.timelineStartDate = startDate;
        this.timelineEndDate = endDate;
        this.timelineRoundOffEndDate = this.getTimelineRoundOffEndDate(this.timelineEndDate);
    }
    /**
     * To validate project start date and end date.
     *
     * @param {string} mode .
     * @param {string} span .
     * @param {Date} startDate .
     * @param {Date} endDate .
     * @returns {void} .
     * @private
     */
    updateScheduleDatesByToolBar(mode, span, startDate, endDate) {
        if (mode === 'Year') {
            if (span === 'prevTimeSpan') {
                if (startDate.getMonth() === 0) {
                    startDate = new Date(startDate.getFullYear() - 1, 0, 1);
                }
                else {
                    startDate = new Date(startDate.getFullYear(), 0, 1);
                }
            }
            else {
                if (endDate.getMonth() === 11) {
                    endDate = new Date(endDate.getFullYear() + 1, 0, 1);
                }
                else {
                    endDate = new Date(endDate.getFullYear(), 12, 1);
                }
            }
        }
        if (mode === 'Month') {
            if (span === 'prevTimeSpan') {
                if (startDate.getDate() === 1) {
                    startDate = new Date(startDate.getFullYear(), startDate.getMonth() - 1, 1);
                }
                else {
                    startDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
                }
            }
            else {
                endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 1);
            }
        }
        if (mode === 'Week') {
            const dayIndex = this.parent.timelineModule.customTimelineSettings.weekStartDay;
            let dayIntervel;
            if (span === 'prevTimeSpan') {
                if (this.parent.timelineSettings.showWeekend) {
                    dayIntervel = startDate.getDay() < dayIndex ? 7 - (dayIndex - startDate.getDay()) :
                        startDate.getDay() - dayIndex;
                    startDate.setHours(0, 0, 0, 0);
                    if (dayIntervel === 0) {
                        startDate.setDate(startDate.getDate() - 7);
                    }
                    else {
                        startDate.setDate(startDate.getDate() - dayIntervel);
                    }
                }
                else {
                    const fullWeek = Array.from({ length: 7 }, (_, index) => index);
                    const workingDays = fullWeek.filter((day) => this.parent.nonWorkingDayIndex.indexOf(day) === -1);
                    startDate.setHours(0, 0, 0, 0);
                    if (this.parent.timelineModule.customTimelineSettings.weekStartDay > 1) {
                        // eslint-disable-next-line no-constant-condition
                        while (true) {
                            startDate.setDate(startDate.getDate() - 1);
                            const currentDay = startDate.getDay();
                            if (workingDays.indexOf(currentDay) !== -1 &&
                                currentDay === this.parent.timelineModule.customTimelineSettings.weekStartDay) {
                                break;
                            }
                        }
                    }
                    else {
                        let count = 0;
                        while (count < workingDays.length) {
                            startDate.setDate(startDate.getDate() - 1);
                            const day = startDate.getDay();
                            if (workingDays.indexOf(day) !== -1) {
                                count++;
                            }
                        }
                    }
                }
            }
            else {
                dayIntervel = endDate.getDay() < dayIndex ? (dayIndex - endDate.getDay()) :
                    (7 - endDate.getDay()) + dayIndex;
                endDate.setHours(0, 0, 0, 0);
                if (dayIntervel === 0) {
                    endDate.setDate(endDate.getDate() + 6);
                }
                else {
                    endDate.setDate(endDate.getDate() + dayIntervel);
                }
            }
        }
        if (mode === 'Day') {
            if (span === 'prevTimeSpan') {
                if (startDate.getHours() === 0) {
                    startDate.setTime(startDate.getTime() - (1000 * 60 * 60 * 24));
                    if (!this.parent.timelineSettings.showWeekend) {
                        while (this.parent.nonWorkingDayIndex.indexOf(startDate.getDay()) !== -1) {
                            startDate.setTime(startDate.getTime() - (1000 * 60 * 60 * 24));
                        }
                    }
                }
                else {
                    startDate.setHours(0);
                }
            }
            else {
                if (endDate.getHours() === 0) {
                    endDate.setTime(endDate.getTime() + (1000 * 60 * 60 * 24));
                    if (!this.parent.timelineSettings.showWeekend) {
                        while (this.parent.nonWorkingDayIndex.indexOf(endDate.getDay()) !== -1) {
                            endDate.setTime(endDate.getTime() + (1000 * 60 * 60 * 24));
                        }
                    }
                }
                else {
                    endDate.setHours(24);
                }
            }
        }
        if (mode === 'Hour') {
            if (span === 'prevTimeSpan') {
                if (startDate.getMinutes() === 0) {
                    startDate.setTime(startDate.getTime() - (1000 * 60 * 60));
                }
                else {
                    startDate.setMinutes(0);
                }
            }
            else {
                if (endDate.getMinutes() === 0) {
                    endDate.setTime(endDate.getTime() + (1000 * 60 * 60));
                }
                else {
                    endDate.setMinutes(60);
                }
            }
        }
        if (mode === 'Minutes') {
            if (span === 'prevTimeSpan') {
                if (startDate.getSeconds() === 0) {
                    startDate.setTime(startDate.getTime() - (1000 * 60));
                }
                else {
                    startDate.setSeconds(0);
                }
            }
            else {
                if (endDate.getSeconds() === 0) {
                    endDate.setTime(endDate.getTime() + (1000 * 60));
                }
                else {
                    endDate.setSeconds(60);
                }
            }
        }
        this.parent.cloneProjectStartDate = this.parent.cloneTimelineStartDate = startDate;
        this.parent.cloneProjectEndDate = this.parent.cloneTimelineEndDate = endDate;
        if (this.parent.timelineSettings.viewStartDate !== 'auto' || this.parent.timelineSettings.viewEndDate !== 'auto') {
            this.parent.setProperties({
                timelineSettings: {
                    viewStartDate: startDate,
                    viewEndDate: endDate
                }
            }, true);
        }
    }
    /**
     * To validate project start date and end date.
     *
     * @param {IGanttData[]} tempArray .
     * @param {string} action .
     * @returns {void} .
     * @private
     */
    updateTimeLineOnEditing(tempArray, action) {
        if (tempArray[0].length >= 1) {
            for (let i = 0; i < tempArray.length; i++) {
                const temp = tempArray[parseInt(i.toString(), 10)];
                const filteredStartDateRecord = temp.filter((pdc) => { return !isNullOrUndefined(pdc.ganttProperties.startDate); });
                const filteredEndDateRecord = temp.filter((pdc) => { return !isNullOrUndefined(pdc.ganttProperties.endDate); });
                let minStartDate = filteredStartDateRecord.length > 0 ?
                    new Date(DataUtil.aggregates.min(filteredStartDateRecord, 'ganttProperties.startDate')) : null;
                const minEndDate = filteredStartDateRecord.length > 0 ?
                    new Date(DataUtil.aggregates.min(filteredEndDateRecord, 'ganttProperties.endDate')) : null;
                let maxEndDate = filteredEndDateRecord.length > 0 ?
                    new Date(DataUtil.aggregates.max(filteredEndDateRecord, 'ganttProperties.endDate')) : null;
                const validStartDate = new Date(this.parent.dataOperation.checkStartDate(this.timelineStartDate).getTime());
                const validEndDate = new Date(this.parent.dataOperation.checkEndDate(this.timelineEndDate).getTime());
                const maxStartLeft = isNullOrUndefined(minStartDate)
                    ? null
                    : this.parent.dataOperation.getTaskLeft(minStartDate, false, filteredStartDateRecord[0].ganttProperties.calendarContext);
                const maxEndLeft = isNullOrUndefined(maxEndDate)
                    ? null
                    : this.parent.dataOperation.getTaskLeft(maxEndDate, false, filteredEndDateRecord[0].ganttProperties.calendarContext);
                const validStartLeft = this.parent.dataOperation.getTaskLeft(validStartDate, false, this.parent.defaultCalendarContext);
                const validEndLeft = this.parent.dataOperation.getTaskLeft(validEndDate, false, this.parent.defaultCalendarContext);
                let isChanged;
                const taskbarModule = this.parent.editModule.taskbarEditModule;
                const startDate = filteredStartDateRecord.length > 0 ?
                    new Date(DataUtil.aggregates.min(filteredStartDateRecord, 'ganttProperties.startDate')).setHours(0, 0, 0, 0) : null;
                const timelineStartDate = new Date(this.timelineStartDate.getTime()).setHours(0, 0, 0, 0);
                if (!isNullOrUndefined(maxStartLeft) && (((((!isNullOrUndefined(taskbarModule)) && ((taskbarModule.taskBarEditAction && taskbarModule.taskBarEditAction !== 'RightResizing') || isNullOrUndefined(taskbarModule.taskBarEditAction)))) &&
                    (startDate <= timelineStartDate))) && (maxStartLeft < this.bottomTierCellWidth || maxStartLeft <= validStartLeft)) {
                    isChanged = 'prevTimeSpan';
                    minStartDate = minStartDate > this.timelineStartDate ? this.timelineStartDate : minStartDate;
                }
                else {
                    minStartDate = this.timelineStartDate;
                }
                if (!isNullOrUndefined(maxEndLeft) && (maxEndLeft >= ((this.parent.enableTimelineVirtualization ?
                    this.wholeTimelineWidth : this.totalTimelineWidth) - this.bottomTierCellWidth) &&
                    maxEndLeft > validEndLeft)) {
                    isChanged = isChanged === 'prevTimeSpan' ? 'both' : 'nextTimeSpan';
                    maxEndDate = maxEndDate < this.timelineEndDate ? this.timelineEndDate : maxEndDate;
                }
                else {
                    maxEndDate = this.timelineEndDate;
                }
                if (isChanged) {
                    this.performTimeSpanAction(isChanged, action, minStartDate, maxEndDate);
                }
                else if (!isNullOrUndefined(temp[0].ganttProperties.segments)) {
                    this.parent.dataOperation.updateWidthLeft(temp[0]);
                }
                if (!isNullOrUndefined(minStartDate) && !isNullOrUndefined(minEndDate) && minEndDate <= minStartDate && (action === 'CellEditing' || action === 'DialogEditing') && this.parent.allowUnscheduledTasks) {
                    minStartDate = new Date(Math.min(minStartDate.getTime(), minEndDate.getTime()));
                    minStartDate = new Date(Math.min(minStartDate.getTime(), this.timelineStartDate.getTime()));
                    this.performTimeSpanAction('prevTimeSpan', action, minStartDate, maxEndDate);
                }
                else if (isNullOrUndefined(minEndDate) && minEndDate <= minStartDate && (action === 'CellEditing' || action === 'DialogEditing') && this.parent.allowUnscheduledTasks && tempArray[0].length === 1 &&
                    !isNullOrUndefined(tempArray[0][0].ganttProperties.endDate) && (isNullOrUndefined(temp[0].ganttProperties.startDate) &&
                    temp[0].ganttProperties.endDate <= this.timelineStartDate)) {
                    minStartDate = new Date(Math.min(tempArray[0][0].ganttProperties.endDate.getTime(), minStartDate.getTime()));
                    this.performTimeSpanAction('prevTimeSpan', action, minStartDate, maxEndDate);
                }
                break;
            }
        }
    }
    /**
     * To validate project start date and end date on editing action
     *
     * @param {string} type .
     * @param {string} isFrom .
     * @param {Date} startDate .
     * @param {Date} endDate .
     * @param {string} mode .
     * @returns {void} .
     * @private
     */
    performTimeSpanAction(type, isFrom, startDate, endDate, mode) {
        mode = !isNullOrUndefined(mode) ? mode : this.parent.timelineModule.topTier === 'None' ?
            this.parent.timelineModule.bottomTier : this.parent.timelineModule.topTier;
        const projectStartDate = new Date(this.parent.cloneProjectStartDate.getTime());
        const projectEndDate = new Date(this.parent.cloneProjectEndDate.getTime());
        if (isFrom !== 'publicMethod' && type === 'both') {
            this.updateScheduleDatesByToolBar(mode, 'prevTimeSpan', startDate, endDate);
            this.updateScheduleDatesByToolBar(mode, 'nextTimeSpan', new Date(this.parent.cloneProjectStartDate.getTime()), endDate);
        }
        else {
            this.updateScheduleDatesByToolBar(mode, type, startDate, endDate);
        }
        const args = this.timeSpanActionEvent('actionBegin', type, isFrom);
        if (!args.cancel) {
            this.restrictRender = true;
            this.performedTimeSpanAction = true;
            const previousScrollLeft = this.parent.ganttChartModule.scrollElement.scrollLeft;
            this.parent.updateProjectDates(args.projectStartDate, args.projectEndDate, args.isTimelineRoundOff, isFrom);
            if (type === 'prevTimeSpan' && isFrom === 'publicMethod') {
                this.parent.ganttChartModule.updateScrollLeft(0);
                this.parent.timelineModule.isZoomToFit = false;
            }
            else if (type === 'nextTimeSpan' && isFrom === 'publicMethod') {
                this.parent.ganttChartModule.updateScrollLeft(this.parent.enableTimelineVirtualization ?
                    this.wholeTimelineWidth : this.totalTimelineWidth);
                this.parent.timelineModule.isZoomToFit = false;
            }
            else if (type === 'nextTimeSpan' && isFrom === 'TaskbarEditing') {
                const currentScrollLeft = document.getElementsByClassName('e-chart-scroll-container e-content')[0].scrollLeft;
                this.parent.element.querySelector('.e-timeline-header-container').scrollLeft = currentScrollLeft;
                this.parent.timelineModule.isZoomToFit = false;
            }
            if (isFrom === 'TaskbarEditing' && this.parent.enableTimelineVirtualization && (this.wholeTimelineWidth > this.parent.element.offsetWidth * 3)) {
                this.parent.ganttChartModule.scrollObject.setScrollLeft(previousScrollLeft);
                this.parent.ganttChartModule.scrollObject.updateContent();
            }
            this.parent.timelineModule.timeSpanActionEvent('actionComplete', type, isFrom);
        }
        else {
            this.parent.cloneProjectStartDate = projectStartDate;
            this.parent.cloneProjectEndDate = projectEndDate;
        }
    }
    adjustEndDateToFillChart(spliterResize) {
        const chartWidth = this.parent.ganttChartModule.chartElement.offsetWidth;
        const perDayWidth = this.parent.perDayWidth;
        let endDate = this.parent.cloneTimelineEndDate;
        // Timeline end date is less than timeline start date
        const startDate = this.parent.cloneTimelineStartDate;
        const setEndDate = (date) => { this.parent.cloneTimelineEndDate = date; };
        const isAutoEnd = !this.parent.timelineSettings.viewEndDate ||
            (typeof this.parent.timelineSettings.viewEndDate === 'string' &&
                this.parent.timelineSettings.viewEndDate.toLowerCase() === 'auto');
        if (startDate && setEndDate && chartWidth && perDayWidth && isAutoEnd) {
            // If endDate is null/undefined, set it to startDate
            if (!endDate) {
                endDate = new Date(startDate.getTime());
            }
            let currentDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24));
            if (!this.parent.timelineSettings.showWeekend) {
                // subtract non-working days to get actual working days between start and end
                const nonWorking = this.calculateNonWorkingDaysBetweenDates(startDate, endDate);
                currentDays = currentDays - nonWorking;
            }
            const visibleDays = Math.ceil(chartWidth / perDayWidth);
            if (currentDays < visibleDays) {
                // Need to add visibleDays working/calendar days from startDate depending on showWeekend
                const newEnd = new Date(startDate.getTime());
                if (!this.parent.timelineSettings.showWeekend) {
                    let addedWorking = 0;
                    while (addedWorking < visibleDays) {
                        newEnd.setDate(newEnd.getDate() + 1);
                        if (this.parent.nonWorkingDayIndex.indexOf(newEnd.getDay()) === -1) {
                            addedWorking++;
                        }
                    }
                }
                else {
                    newEnd.setDate(newEnd.getDate() + visibleDays);
                }
                setEndDate(newEnd);
                // Only execute this if called from splitter action
                if (spliterResize) {
                    this.updateTimelineAfterZooming(endDate, true);
                }
            }
        }
    }
    updateTimelineSettingsAfterZooming() {
        this.parent.setProperties({
            timelineSettings: {
                timelineViewMode: this.customTimelineSettings.timelineViewMode,
                timelineUnitSize: this.customTimelineSettings.timelineUnitSize,
                bottomTier: {
                    unit: this.customTimelineSettings.bottomTier.unit,
                    format: this.customTimelineSettings.bottomTier.format,
                    count: this.customTimelineSettings.bottomTier.count
                },
                topTier: {
                    unit: this.customTimelineSettings.topTier.unit,
                    format: this.customTimelineSettings.topTier.format,
                    count: this.customTimelineSettings.topTier.count
                },
                weekStartDay: this.customTimelineSettings.weekStartDay,
                weekendBackground: this.customTimelineSettings.weekendBackground
            }
        }, true);
    }
    /**
     * To validate project start date and end date.
     *
     * @param {string} eventType .
     * @param {string} requestType .
     * @param {string} isFrom .
     * @returns {void}
     * @private
     */
    timeSpanActionEvent(eventType, requestType, isFrom) {
        const args = {};
        args.projectStartDate = new Date(this.parent.cloneProjectStartDate.getTime());
        args.projectEndDate = new Date(this.parent.cloneProjectEndDate.getTime());
        args.requestType = isFrom === 'publicMethod' ? requestType : isFrom === 'beforeAdd' ?
            'TimelineRefreshOnAdd' : isFrom === 'TaskbarEditing' ? 'TimelineRefreshOnEdit' : requestType;
        if (eventType === 'actionBegin') {
            args.isTimelineRoundOff = this.parent.isTimelineRoundOff;
            args.cancel = false;
        }
        args.action = 'TimescaleUpdate';
        this.parent.trigger(eventType, args);
        return args;
    }
}
